import type { Recipe } from '@/types';
import { generateId } from '@/lib/utils';
import { SCHEMA_VERSION } from '@/types';

const now = new Date().toISOString();

export const SEED_RECIPES: Recipe[] = [
  {
    id: generateId(),
    name: 'Classic Spaghetti Carbonara',
    description: 'A rich and creamy Roman pasta dish made with eggs, Pecorino Romano cheese, crispy beef bacon, and black pepper. Simple ingredients, extraordinary flavor.',
    imageUrl: '/recipe-images/seed/classic-spaghetti-carbonara.webp',
    prepTime: 15 * 60, // 15 minutes
    cookTime: 20 * 60, // 20 minutes
    totalTime: 35 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'italian',
    tags: ['pasta', 'quick', 'classic', 'comfort food'],
    dietaryTags: ['halal'],
    ingredients: [
      { id: generateId(), name: 'Spaghetti', amount: 400, unit: 'g' },
      { id: generateId(), name: 'Beef bacon', amount: 200, unit: 'g', notes: 'cut into small cubes' },
      { id: generateId(), name: 'Egg yolks', amount: 4, unit: 'piece' },
      { id: generateId(), name: 'Whole eggs', amount: 2, unit: 'piece' },
      { id: generateId(), name: 'Pecorino Romano', amount: 100, unit: 'g', notes: 'finely grated' },
      { id: generateId(), name: 'Black pepper', amount: 2, unit: 'tsp', notes: 'freshly ground' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'to taste', notes: 'for pasta water' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Bring a large pot of well-salted water to a boil.' },
      { id: generateId(), order: 2, instruction: 'While water heats, cut beef bacon into small cubes (about 1cm).' },
      { id: generateId(), order: 3, instruction: 'In a bowl, whisk together egg yolks, whole eggs, grated Pecorino, and generous black pepper.' },
      { id: generateId(), order: 4, instruction: 'Cook beef bacon in a large skillet over medium heat until fat renders and meat is crispy, about 8-10 minutes.', timerDuration: 600, timerLabel: 'Beef bacon' },
      { id: generateId(), order: 5, instruction: 'Cook spaghetti according to package directions until al dente. Reserve 1 cup pasta water before draining.', timerDuration: 480, timerLabel: 'Pasta' },
      { id: generateId(), order: 6, instruction: 'Remove pan from heat. Add drained pasta to the pan with the beef bacon.' },
      { id: generateId(), order: 7, instruction: 'Working quickly off the heat, pour egg mixture over pasta and toss vigorously, adding pasta water as needed to create a creamy sauce.' },
      { id: generateId(), order: 8, instruction: 'Serve immediately with extra Pecorino and black pepper on top.' },
    ],
    notes: 'The key is to work off the heat when adding the eggs to prevent scrambling. The residual heat will cook the eggs into a silky sauce. Beef bacon gives a similar crispy, savory depth as the traditional Italian cured meat.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Thai Green Curry',
    description: 'A fragrant and spicy Thai curry with tender chicken, crisp vegetables, and aromatic green curry paste in creamy coconut milk.',
    imageUrl: '/recipe-images/seed/thai-green-curry.webp',
    prepTime: 20 * 60,
    cookTime: 25 * 60,
    totalTime: 45 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'thai',
    tags: ['curry', 'spicy', 'asian', 'one-pot'],
    dietaryTags: ['gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chicken breast', amount: 500, unit: 'g', notes: 'sliced into strips' },
      { id: generateId(), name: 'Green curry paste', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Coconut milk', amount: 400, unit: 'ml', notes: '1 can' },
      { id: generateId(), name: 'Thai basil leaves', amount: 1, unit: 'cup' },
      { id: generateId(), name: 'Bamboo shoots', amount: 200, unit: 'g' },
      { id: generateId(), name: 'Thai eggplant', amount: 4, unit: 'piece', notes: 'quartered', optional: true },
      { id: generateId(), name: 'Fish sauce', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Palm sugar', amount: 1, unit: 'tbsp', notes: 'or brown sugar' },
      { id: generateId(), name: 'Kaffir lime leaves', amount: 4, unit: 'piece', notes: 'torn' },
      { id: generateId(), name: 'Red chili', amount: 2, unit: 'piece', notes: 'sliced, for garnish' },
      { id: generateId(), name: 'Vegetable oil', amount: 2, unit: 'tbsp' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Heat oil in a wok or large pan over medium-high heat.' },
      { id: generateId(), order: 2, instruction: 'Add green curry paste and fry for 1-2 minutes until fragrant.', timerDuration: 120, timerLabel: 'Curry paste' },
      { id: generateId(), order: 3, instruction: 'Add half the coconut milk and cook until oil separates and curry bubbles, about 3 minutes.' },
      { id: generateId(), order: 4, instruction: 'Add chicken and cook until no longer pink on the outside, about 5 minutes.', timerDuration: 300, timerLabel: 'Chicken' },
      { id: generateId(), order: 5, instruction: 'Pour in remaining coconut milk, add bamboo shoots, eggplant, and kaffir lime leaves.' },
      { id: generateId(), order: 6, instruction: 'Simmer for 10-12 minutes until vegetables are tender and chicken is cooked through.', timerDuration: 720, timerLabel: 'Simmer' },
      { id: generateId(), order: 7, instruction: 'Season with fish sauce and palm sugar. Adjust to taste.' },
      { id: generateId(), order: 8, instruction: 'Stir in Thai basil leaves just before serving. Garnish with sliced chilies.' },
    ],
    notes: 'Serve with jasmine rice. For a vegetarian version, substitute chicken with tofu and use soy sauce instead of fish sauce.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Classic French Omelette',
    description: 'A perfectly cooked French omelette with a soft, creamy interior and delicate pale exterior. Master this technique for an elegant breakfast.',
    imageUrl: '/recipe-images/seed/classic-french-omelette.webp',
    prepTime: 5 * 60,
    cookTime: 3 * 60,
    totalTime: 8 * 60,
    servings: 1,
    difficulty: 'hard',
    cuisine: 'french',
    tags: ['breakfast', 'eggs', 'quick', 'technique'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Eggs', amount: 3, unit: 'piece', notes: 'room temperature' },
      { id: generateId(), name: 'Unsalted butter', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'pinch' },
      { id: generateId(), name: 'Fresh chives', amount: 1, unit: 'tbsp', notes: 'finely chopped', optional: true },
      { id: generateId(), name: 'White pepper', amount: 1, unit: 'pinch', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Crack eggs into a bowl and beat with a fork until yolks and whites are fully combined. Season with salt.' },
      { id: generateId(), order: 2, instruction: 'Heat an 8-inch non-stick pan over medium-high heat. Add butter and swirl to coat.' },
      { id: generateId(), order: 3, instruction: 'When butter foams but before it browns, pour in the eggs.' },
      { id: generateId(), order: 4, instruction: 'Immediately stir eggs with a fork or chopsticks while shaking the pan. Work quickly for 20-30 seconds.', timerDuration: 30, timerLabel: 'Stirring' },
      { id: generateId(), order: 5, instruction: 'When eggs are still slightly wet on top, stop stirring. Let set for 10 seconds.' },
      { id: generateId(), order: 6, instruction: 'Tilt pan away from you and use fork to fold the far edge of the omelette to the center.' },
      { id: generateId(), order: 7, instruction: 'Slide omelette to the edge of the pan and fold onto itself as you roll it onto the plate.' },
      { id: generateId(), order: 8, instruction: 'Rub surface with a cold piece of butter for shine. Sprinkle with chives if using.' },
    ],
    notes: 'The key to a perfect French omelette is high heat, constant movement, and knowing when to stop. The interior should be baveuse (slightly runny). Practice makes perfect!',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Honey Garlic Salmon',
    description: 'Tender pan-seared salmon glazed with a sticky, sweet, and savory honey garlic sauce. Ready in under 20 minutes!',
    imageUrl: '/recipe-images/seed/honey-garlic-salmon.webp',
    prepTime: 10 * 60,
    cookTime: 12 * 60,
    totalTime: 22 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'american',
    tags: ['seafood', 'quick', 'healthy', 'weeknight'],
    dietaryTags: ['gluten-free', 'pescatarian', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Salmon fillets', amount: 4, unit: 'piece', notes: '6 oz each, skin-on' },
      { id: generateId(), name: 'Honey', amount: 4, unit: 'tbsp' },
      { id: generateId(), name: 'Soy sauce', amount: 3, unit: 'tbsp', notes: 'or coconut aminos for GF' },
      { id: generateId(), name: 'Garlic cloves', amount: 4, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Rice vinegar', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Olive oil', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Green onions', amount: 2, unit: 'piece', notes: 'sliced, for garnish' },
      { id: generateId(), name: 'Sesame seeds', amount: 1, unit: 'tsp', notes: 'for garnish' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Pat salmon fillets dry with paper towels. Season with pepper.' },
      { id: generateId(), order: 2, instruction: 'Mix honey, soy sauce, minced garlic, and rice vinegar in a small bowl.' },
      { id: generateId(), order: 3, instruction: 'Heat olive oil in a large skillet over medium-high heat.' },
      { id: generateId(), order: 4, instruction: 'Place salmon skin-side up in the pan. Sear for 4 minutes until golden.', timerDuration: 240, timerLabel: 'Sear side 1' },
      { id: generateId(), order: 5, instruction: 'Flip salmon and cook for another 3 minutes.', timerDuration: 180, timerLabel: 'Sear side 2' },
      { id: generateId(), order: 6, instruction: 'Pour honey garlic sauce around the salmon. Let it bubble and thicken for 2 minutes, spooning sauce over the fish.', timerDuration: 120, timerLabel: 'Glaze' },
      { id: generateId(), order: 7, instruction: 'Transfer to plates, spoon remaining sauce over the top.' },
      { id: generateId(), order: 8, instruction: 'Garnish with sliced green onions and sesame seeds. Serve immediately.' },
    ],
    notes: 'Don\'t move the salmon while it\'s searing - let it develop a nice crust. Pairs perfectly with steamed rice and roasted vegetables.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Mediterranean Chickpea Salad',
    description: 'A refreshing, protein-packed salad with chickpeas, cucumber, tomatoes, feta, and a zesty lemon dressing. Perfect for meal prep!',
    imageUrl: '/recipe-images/seed/mediterranean-chickpea-salad.webp',
    prepTime: 15 * 60,
    cookTime: 0,
    totalTime: 15 * 60,
    servings: 6,
    difficulty: 'easy',
    cuisine: 'mediterranean',
    tags: ['salad', 'healthy', 'meal-prep', 'no-cook'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chickpeas', amount: 400, unit: 'g', notes: 'canned, drained and rinsed' },
      { id: generateId(), name: 'Cucumber', amount: 1, unit: 'piece', notes: 'diced' },
      { id: generateId(), name: 'Cherry tomatoes', amount: 250, unit: 'g', notes: 'halved' },
      { id: generateId(), name: 'Red onion', amount: 0.5, unit: 'piece', notes: 'finely diced' },
      { id: generateId(), name: 'Kalamata olives', amount: 100, unit: 'g', notes: 'pitted and halved' },
      { id: generateId(), name: 'Feta cheese', amount: 150, unit: 'g', notes: 'crumbled' },
      { id: generateId(), name: 'Fresh parsley', amount: 0.5, unit: 'cup', notes: 'chopped' },
      { id: generateId(), name: 'Extra virgin olive oil', amount: 4, unit: 'tbsp', group: 'Dressing' },
      { id: generateId(), name: 'Lemon juice', amount: 3, unit: 'tbsp', group: 'Dressing' },
      { id: generateId(), name: 'Garlic clove', amount: 1, unit: 'clove', notes: 'minced', group: 'Dressing' },
      { id: generateId(), name: 'Dried oregano', amount: 1, unit: 'tsp', group: 'Dressing' },
      { id: generateId(), name: 'Salt and pepper', amount: 1, unit: 'to taste', group: 'Dressing' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Drain and rinse chickpeas. Pat dry with paper towels for best texture.' },
      { id: generateId(), order: 2, instruction: 'Dice cucumber, halve tomatoes, finely dice red onion, and chop parsley.' },
      { id: generateId(), order: 3, instruction: 'In a large bowl, combine chickpeas, cucumber, tomatoes, red onion, olives, and most of the feta.' },
      { id: generateId(), order: 4, instruction: 'Make the dressing: whisk together olive oil, lemon juice, minced garlic, oregano, salt, and pepper.' },
      { id: generateId(), order: 5, instruction: 'Pour dressing over the salad and toss gently to combine.' },
      { id: generateId(), order: 6, instruction: 'Add parsley and toss again. Top with remaining feta.' },
      { id: generateId(), order: 7, instruction: 'Serve immediately or refrigerate for up to 3 days. Flavors improve after chilling!' },
    ],
    notes: 'For meal prep, store the dressing separately and add just before serving. You can add grilled chicken or shrimp for extra protein.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Japanese Miso Ramen',
    description: 'A soul-warming bowl of rich miso broth with chewy noodles, tender sliced chicken, soft-boiled egg, and all the classic toppings.',
    imageUrl: '/recipe-images/seed/japanese-miso-ramen.webp',
    prepTime: 30 * 60,
    cookTime: 45 * 60,
    totalTime: 75 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'japanese',
    tags: ['soup', 'noodles', 'comfort food', 'asian'],
    dietaryTags: ['halal'],
    ingredients: [
      { id: generateId(), name: 'Ramen noodles', amount: 400, unit: 'g', notes: 'fresh or dried' },
      { id: generateId(), name: 'Chicken broth', amount: 1.5, unit: 'l' },
      { id: generateId(), name: 'White miso paste', amount: 4, unit: 'tbsp' },
      { id: generateId(), name: 'Soy sauce', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Rice vinegar', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tbsp', notes: 'to replace mirin sweetness' },
      { id: generateId(), name: 'Sesame oil', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'tbsp', notes: 'freshly grated' },
      { id: generateId(), name: 'Chicken thigh', amount: 300, unit: 'g', notes: 'boneless, sliced', group: 'Toppings' },
      { id: generateId(), name: 'Soft-boiled eggs', amount: 4, unit: 'piece', group: 'Toppings' },
      { id: generateId(), name: 'Corn kernels', amount: 100, unit: 'g', group: 'Toppings' },
      { id: generateId(), name: 'Bean sprouts', amount: 100, unit: 'g', group: 'Toppings' },
      { id: generateId(), name: 'Green onions', amount: 4, unit: 'piece', notes: 'sliced', group: 'Toppings' },
      { id: generateId(), name: 'Nori sheets', amount: 4, unit: 'piece', group: 'Toppings' },
      { id: generateId(), name: 'Sesame seeds', amount: 1, unit: 'tbsp', group: 'Toppings' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Make soft-boiled eggs: bring water to boil, lower eggs, cook 6.5 minutes, then ice bath.', timerDuration: 390, timerLabel: 'Eggs' },
      { id: generateId(), order: 2, instruction: 'Season chicken thigh with soy sauce. Sear in a hot pan until golden and cooked through on both sides, about 4 minutes per side. Slice and set aside.', timerDuration: 480, timerLabel: 'Chicken' },
      { id: generateId(), order: 3, instruction: 'In a large pot, heat sesame oil. Sauté garlic and ginger until fragrant, about 1 minute.' },
      { id: generateId(), order: 4, instruction: 'Add chicken broth and bring to a simmer. Cook for 15 minutes to develop flavor.', timerDuration: 900, timerLabel: 'Broth' },
      { id: generateId(), order: 5, instruction: 'In a small bowl, whisk miso paste with some hot broth until smooth. Stir back into the pot.' },
      { id: generateId(), order: 6, instruction: 'Add soy sauce, rice vinegar, and sugar. Taste and adjust seasoning. Keep warm.' },
      { id: generateId(), order: 7, instruction: 'Cook ramen noodles according to package directions. Drain well.', timerDuration: 240, timerLabel: 'Noodles' },
      { id: generateId(), order: 8, instruction: 'Divide noodles among 4 bowls. Ladle hot broth over noodles.' },
      { id: generateId(), order: 9, instruction: 'Arrange toppings: chicken slices, halved soft-boiled egg, corn, bean sprouts, green onions, nori.' },
      { id: generateId(), order: 10, instruction: 'Sprinkle with sesame seeds and serve immediately while piping hot.' },
    ],
    notes: 'The key to great ramen is a flavorful broth. Don\'t skip the step of blooming the miso - it prevents clumps and deepens the flavor.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Classic Beef Tacos',
    description: 'Authentic Mexican street-style tacos with perfectly seasoned beef, fresh salsa, and all your favorite toppings on warm corn tortillas.',
    imageUrl: '/recipe-images/seed/classic-beef-tacos.webp',
    prepTime: 15 * 60,
    cookTime: 15 * 60,
    totalTime: 30 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'mexican',
    tags: ['tacos', 'quick', 'family-friendly', 'weeknight'],
    dietaryTags: ['gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Ground beef', amount: 500, unit: 'g', notes: '80/20 blend' },
      { id: generateId(), name: 'Corn tortillas', amount: 12, unit: 'piece', notes: 'small, street taco size' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'diced' },
      { id: generateId(), name: 'Chili powder', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Cumin', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Paprika', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Garlic powder', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Lime juice', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Fresh cilantro', amount: 0.5, unit: 'cup', notes: 'chopped', group: 'Toppings' },
      { id: generateId(), name: 'White onion', amount: 0.5, unit: 'piece', notes: 'finely diced', group: 'Toppings' },
      { id: generateId(), name: 'Salsa verde', amount: 0.5, unit: 'cup', group: 'Toppings' },
      { id: generateId(), name: 'Lime wedges', amount: 4, unit: 'piece', group: 'Toppings' },
      { id: generateId(), name: 'Cotija cheese', amount: 100, unit: 'g', notes: 'crumbled', group: 'Toppings', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Mix chili powder, cumin, paprika, garlic powder, and salt in a small bowl.' },
      { id: generateId(), order: 2, instruction: 'Heat a large skillet over medium-high heat. Add ground beef and diced onion.' },
      { id: generateId(), order: 3, instruction: 'Cook, breaking up meat, until browned and no pink remains, about 8 minutes.', timerDuration: 480, timerLabel: 'Brown meat' },
      { id: generateId(), order: 4, instruction: 'Add spice mixture and lime juice. Stir well and cook for another 2 minutes.', timerDuration: 120, timerLabel: 'Season' },
      { id: generateId(), order: 5, instruction: 'Warm tortillas: heat in a dry skillet for 30 seconds per side, or wrap in damp paper towel and microwave 30 seconds.' },
      { id: generateId(), order: 6, instruction: 'Assemble tacos: double up tortillas, add seasoned beef.' },
      { id: generateId(), order: 7, instruction: 'Top with fresh cilantro, diced white onion, salsa verde, and cotija if using.' },
      { id: generateId(), order: 8, instruction: 'Serve immediately with lime wedges on the side.' },
    ],
    notes: 'For extra flavor, char the tortillas directly over a gas flame for a few seconds. You can also use this seasoned meat for burritos, nachos, or taco salads.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Vegetable Stir-Fry with Tofu',
    description: 'A colorful, healthy stir-fry loaded with crispy tofu and fresh vegetables in a savory-sweet sauce. Ready in 25 minutes!',
    imageUrl: '/recipe-images/seed/vegetable-stir-fry-with-tofu.webp',
    prepTime: 15 * 60,
    cookTime: 10 * 60,
    totalTime: 25 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'chinese',
    tags: ['stir-fry', 'healthy', 'quick', 'weeknight'],
    dietaryTags: ['vegan', 'vegetarian', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Extra-firm tofu', amount: 400, unit: 'g', notes: 'pressed and cubed' },
      { id: generateId(), name: 'Broccoli florets', amount: 200, unit: 'g' },
      { id: generateId(), name: 'Bell peppers', amount: 2, unit: 'piece', notes: 'mixed colors, sliced' },
      { id: generateId(), name: 'Snap peas', amount: 150, unit: 'g' },
      { id: generateId(), name: 'Carrots', amount: 2, unit: 'piece', notes: 'sliced thin on diagonal' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'tbsp', notes: 'minced' },
      { id: generateId(), name: 'Soy sauce', amount: 3, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Rice vinegar', amount: 1, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Maple syrup', amount: 1, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Sesame oil', amount: 1, unit: 'tsp', group: 'Sauce' },
      { id: generateId(), name: 'Cornstarch', amount: 1, unit: 'tsp', group: 'Sauce' },
      { id: generateId(), name: 'Vegetable oil', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Sesame seeds', amount: 1, unit: 'tbsp', notes: 'for garnish' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Press tofu for at least 15 minutes to remove excess water. Cut into 1-inch cubes.' },
      { id: generateId(), order: 2, instruction: 'Mix sauce ingredients in a small bowl: soy sauce, rice vinegar, maple syrup, sesame oil, and cornstarch.' },
      { id: generateId(), order: 3, instruction: 'Heat 2 tbsp oil in a wok or large skillet over high heat until smoking.' },
      { id: generateId(), order: 4, instruction: 'Add tofu in a single layer. Cook without stirring for 3 minutes until golden. Flip and cook another 2 minutes.', timerDuration: 300, timerLabel: 'Tofu' },
      { id: generateId(), order: 5, instruction: 'Remove tofu and set aside. Add remaining oil to the wok.' },
      { id: generateId(), order: 6, instruction: 'Add carrots and broccoli. Stir-fry for 2 minutes.', timerDuration: 120, timerLabel: 'Hard veggies' },
      { id: generateId(), order: 7, instruction: 'Add bell peppers, snap peas, garlic, and ginger. Stir-fry for 2 more minutes.', timerDuration: 120, timerLabel: 'Soft veggies' },
      { id: generateId(), order: 8, instruction: 'Return tofu to wok. Pour in sauce and toss everything together until sauce thickens and coats evenly.' },
      { id: generateId(), order: 9, instruction: 'Garnish with sesame seeds. Serve over rice or noodles.' },
    ],
    notes: 'The key to crispy tofu is pressing out the water and not moving it in the pan. High heat is essential for a good stir-fry!',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Butter Chicken (Murgh Makhani)',
    description: 'India\'s most beloved curry — tender chicken pieces in a velvety tomato-butter sauce spiced with garam masala, fenugreek, and cream. Rich, aromatic, and deeply comforting.',
    imageUrl: '/recipe-images/seed/butter-chicken-murgh-makhani.webp',
    prepTime: 20 * 60,
    cookTime: 30 * 60,
    totalTime: 50 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'indian',
    tags: ['curry', 'comfort food', 'dinner', 'classic'],
    dietaryTags: ['gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chicken thighs', amount: 700, unit: 'g', notes: 'boneless, skinless, cut into chunks' },
      { id: generateId(), name: 'Plain yogurt', amount: 0.5, unit: 'cup', notes: 'full-fat', group: 'Marinade' },
      { id: generateId(), name: 'Lemon juice', amount: 1, unit: 'tbsp', group: 'Marinade' },
      { id: generateId(), name: 'Garam masala', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Turmeric', amount: 0.5, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Chili powder', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Unsalted butter', amount: 3, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Vegetable oil', amount: 1, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'finely diced', group: 'Sauce' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced', group: 'Sauce' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'tbsp', notes: 'freshly grated', group: 'Sauce' },
      { id: generateId(), name: 'Tomato puree', amount: 400, unit: 'g', notes: '1 can crushed tomatoes', group: 'Sauce' },
      { id: generateId(), name: 'Garam masala', amount: 1.5, unit: 'tsp', group: 'Sauce' },
      { id: generateId(), name: 'Dried fenugreek leaves', amount: 1, unit: 'tsp', notes: 'crushed between palms', group: 'Sauce' },
      { id: generateId(), name: 'Heavy cream', amount: 0.5, unit: 'cup', group: 'Sauce' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tsp', group: 'Sauce' },
      { id: generateId(), name: 'Fresh cilantro', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Combine chicken chunks with yogurt, lemon juice, garam masala, turmeric, chili powder, and salt. Mix well and marinate for at least 15 minutes (or up to overnight in the fridge).' },
      { id: generateId(), order: 2, instruction: 'Heat oil in a large heavy-bottomed pan over medium-high heat. Add marinated chicken and sear until browned on all sides, about 5 minutes. Remove and set aside.', timerDuration: 300, timerLabel: 'Sear chicken' },
      { id: generateId(), order: 3, instruction: 'In the same pan, melt butter over medium heat. Add diced onion and cook until soft and golden, about 5 minutes.', timerDuration: 300, timerLabel: 'Onions' },
      { id: generateId(), order: 4, instruction: 'Add garlic and ginger. Stir for 1 minute until fragrant.' },
      { id: generateId(), order: 5, instruction: 'Pour in tomato puree, garam masala, and fenugreek leaves. Stir well and simmer for 10 minutes until sauce darkens and thickens.', timerDuration: 600, timerLabel: 'Sauce' },
      { id: generateId(), order: 6, instruction: 'Return chicken to the sauce. Stir to coat and simmer for 10 minutes until chicken is cooked through.', timerDuration: 600, timerLabel: 'Chicken' },
      { id: generateId(), order: 7, instruction: 'Stir in heavy cream and sugar. Simmer for 2 more minutes. Taste and adjust salt.' },
      { id: generateId(), order: 8, instruction: 'Garnish with fresh cilantro. Serve with basmati rice or warm naan bread.' },
    ],
    notes: 'Marinating overnight makes a noticeable difference. Dried fenugreek leaves (kasuri methi) are the secret ingredient — don\'t skip them. Find them at any Indian grocery store.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Korean Bibimbap',
    description: 'A vibrant Korean rice bowl topped with sautéed vegetables, seasoned beef, a fried egg, and spicy gochujang sauce. Mix it all together for a perfect bite every time.',
    imageUrl: '/recipe-images/seed/korean-bibimbap.webp',
    prepTime: 25 * 60,
    cookTime: 20 * 60,
    totalTime: 45 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'korean',
    tags: ['rice', 'bowl', 'healthy', 'weeknight'],
    dietaryTags: ['dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Short-grain rice', amount: 2, unit: 'cup', notes: 'rinsed 3 times', group: 'Rice' },
      { id: generateId(), name: 'Water', amount: 2.5, unit: 'cup', group: 'Rice' },
      { id: generateId(), name: 'Sesame oil', amount: 1, unit: 'tsp', group: 'Rice' },
      { id: generateId(), name: 'Beef sirloin', amount: 300, unit: 'g', notes: 'thinly sliced', group: 'Beef' },
      { id: generateId(), name: 'Soy sauce', amount: 2, unit: 'tbsp', group: 'Beef' },
      { id: generateId(), name: 'Sesame oil', amount: 1, unit: 'tsp', group: 'Beef' },
      { id: generateId(), name: 'Garlic', amount: 2, unit: 'clove', notes: 'minced', group: 'Beef' },
      { id: generateId(), name: 'Brown sugar', amount: 1, unit: 'tsp', group: 'Beef' },
      { id: generateId(), name: 'Spinach', amount: 200, unit: 'g', group: 'Vegetables' },
      { id: generateId(), name: 'Carrots', amount: 2, unit: 'piece', notes: 'julienned', group: 'Vegetables' },
      { id: generateId(), name: 'Zucchini', amount: 1, unit: 'piece', notes: 'half-moon slices', group: 'Vegetables' },
      { id: generateId(), name: 'Bean sprouts', amount: 150, unit: 'g', group: 'Vegetables' },
      { id: generateId(), name: 'Shiitake mushrooms', amount: 100, unit: 'g', notes: 'sliced', group: 'Vegetables' },
      { id: generateId(), name: 'Eggs', amount: 4, unit: 'piece', group: 'Toppings' },
      { id: generateId(), name: 'Gochujang', amount: 4, unit: 'tbsp', group: 'Toppings' },
      { id: generateId(), name: 'Sesame seeds', amount: 1, unit: 'tbsp', group: 'Toppings', optional: true },
      { id: generateId(), name: 'Green onions', amount: 2, unit: 'piece', notes: 'sliced', group: 'Toppings', optional: true },
      { id: generateId(), name: 'Vegetable oil', amount: 3, unit: 'tbsp' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Cook rice according to your preferred method. Once done, stir in 1 tsp sesame oil and keep warm.' },
      { id: generateId(), order: 2, instruction: 'Marinate beef: toss sliced sirloin with soy sauce, sesame oil, minced garlic, and brown sugar. Set aside for at least 10 minutes.' },
      { id: generateId(), order: 3, instruction: 'Julienne carrots, slice zucchini into half-moons, and slice mushrooms.' },
      { id: generateId(), order: 4, instruction: 'Blanch spinach in boiling water for 30 seconds. Drain, squeeze dry, and season with a pinch of salt and sesame oil.', timerDuration: 30, timerLabel: 'Spinach' },
      { id: generateId(), order: 5, instruction: 'Heat 1 tsp oil in a skillet over high heat. Sauté carrots for 2 minutes until slightly softened. Set aside.', timerDuration: 120, timerLabel: 'Carrots' },
      { id: generateId(), order: 6, instruction: 'In the same skillet, sauté zucchini for 2 minutes. Set aside. Repeat with mushrooms and bean sprouts, 2 minutes each.', timerDuration: 120, timerLabel: 'Zucchini' },
      { id: generateId(), order: 7, instruction: 'Sear marinated beef over high heat for 3-4 minutes until caramelized and cooked through. Set aside.', timerDuration: 240, timerLabel: 'Beef' },
      { id: generateId(), order: 8, instruction: 'Fry eggs sunny-side up: heat oil in a non-stick pan over medium heat, crack eggs in, and cook until whites are set but yolks are still runny, about 3 minutes.', timerDuration: 180, timerLabel: 'Eggs' },
      { id: generateId(), order: 9, instruction: 'Assemble bowls: divide hot rice among 4 bowls. Arrange each vegetable and the beef in separate sections on top of the rice.' },
      { id: generateId(), order: 10, instruction: 'Place a fried egg in the center of each bowl. Add a tablespoon of gochujang, sprinkle with sesame seeds and green onions. Mix everything together before eating.' },
    ],
    notes: 'The key to great bibimbap is cooking each component separately so they keep their distinct flavors and textures. For a crispy rice bottom (dolsot style), heat stone bowls first and press the rice in before topping.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Vietnamese Beef Pho',
    description: 'A fragrant, deeply savory Vietnamese soup with silky rice noodles, tender beef slices, and an aromatic broth infused with star anise, cinnamon, and charred ginger.',
    imageUrl: '/recipe-images/seed/vietnamese-beef-pho.webp',
    prepTime: 20 * 60,
    cookTime: 40 * 60,
    totalTime: 60 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'vietnamese',
    tags: ['soup', 'noodles', 'comfort food', 'classic'],
    dietaryTags: ['dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Beef broth', amount: 2, unit: 'l', notes: 'good quality', group: 'Broth' },
      { id: generateId(), name: 'Star anise', amount: 3, unit: 'piece', group: 'Broth' },
      { id: generateId(), name: 'Cinnamon stick', amount: 1, unit: 'piece', group: 'Broth' },
      { id: generateId(), name: 'Whole cloves', amount: 3, unit: 'piece', group: 'Broth' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'piece', notes: '3-inch piece, halved and charred', group: 'Broth' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'halved and charred', group: 'Broth' },
      { id: generateId(), name: 'Fish sauce', amount: 3, unit: 'tbsp', group: 'Broth' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tsp', group: 'Broth' },
      { id: generateId(), name: 'Rice noodles', amount: 400, unit: 'g', notes: 'dried, flat, medium width' },
      { id: generateId(), name: 'Beef sirloin', amount: 300, unit: 'g', notes: 'very thinly sliced (freeze 20 min for easier slicing)' },
      { id: generateId(), name: 'Bean sprouts', amount: 150, unit: 'g', group: 'Garnish' },
      { id: generateId(), name: 'Fresh Thai basil', amount: 1, unit: 'cup', notes: 'leaves picked', group: 'Garnish' },
      { id: generateId(), name: 'Lime', amount: 2, unit: 'piece', notes: 'cut into wedges', group: 'Garnish' },
      { id: generateId(), name: 'Jalapeño or bird\'s eye chili', amount: 2, unit: 'piece', notes: 'thinly sliced', group: 'Garnish' },
      { id: generateId(), name: 'Fresh cilantro', amount: 0.5, unit: 'cup', group: 'Garnish' },
      { id: generateId(), name: 'Hoisin sauce', amount: 2, unit: 'tbsp', notes: 'for serving', group: 'Garnish', optional: true },
      { id: generateId(), name: 'Sriracha', amount: 2, unit: 'tbsp', notes: 'for serving', group: 'Garnish', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Char ginger and onion: place halved ginger and onion cut-side down in a dry skillet over high heat. Cook until deeply charred, about 4 minutes. This adds smoky depth to the broth.', timerDuration: 240, timerLabel: 'Char' },
      { id: generateId(), order: 2, instruction: 'Toast spices: in the same dry skillet, add star anise, cinnamon stick, and cloves. Toast for 1-2 minutes until fragrant, shaking often.' },
      { id: generateId(), order: 3, instruction: 'Bring beef broth to a boil in a large pot. Add charred ginger, charred onion, toasted spices, fish sauce, and sugar.' },
      { id: generateId(), order: 4, instruction: 'Reduce heat and simmer broth for 30 minutes to develop flavor. Skim any foam that rises to the surface.', timerDuration: 1800, timerLabel: 'Broth' },
      { id: generateId(), order: 5, instruction: 'While broth simmers, soak rice noodles in warm water for 15 minutes until pliable. Drain.', timerDuration: 900, timerLabel: 'Noodles soak' },
      { id: generateId(), order: 6, instruction: 'Bring a separate pot of water to a boil. Cook soaked noodles for 30-60 seconds until tender. Drain and divide among 4 large bowls.' },
      { id: generateId(), order: 7, instruction: 'Strain broth through a fine-mesh sieve, discarding solids. Return to pot and keep at a rolling boil.' },
      { id: generateId(), order: 8, instruction: 'Arrange raw beef slices over the noodles in each bowl. Ladle boiling broth directly over the beef — the heat will cook the thin slices instantly.' },
      { id: generateId(), order: 9, instruction: 'Serve immediately with bean sprouts, Thai basil, lime wedges, sliced chilies, cilantro, and hoisin and sriracha on the side.' },
    ],
    notes: 'The key is boiling-hot broth poured directly over raw beef — it cooks the slices to perfect tenderness. Freeze the sirloin for 20 minutes before slicing for paper-thin cuts.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Shakshuka',
    description: 'Eggs poached in a spiced, smoky tomato-pepper sauce — a Middle Eastern breakfast staple that\'s just as perfect for dinner. Scoop it up with crusty bread.',
    imageUrl: '/recipe-images/seed/shakshuka.webp',
    prepTime: 10 * 60,
    cookTime: 25 * 60,
    totalTime: 35 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'middle-eastern',
    tags: ['breakfast', 'eggs', 'one-pot', 'comfort food'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Olive oil', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'diced' },
      { id: generateId(), name: 'Red bell pepper', amount: 1, unit: 'piece', notes: 'diced' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Cumin', amount: 1.5, unit: 'tsp' },
      { id: generateId(), name: 'Paprika', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Smoked paprika', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Cayenne pepper', amount: 0.25, unit: 'tsp', notes: 'or more to taste' },
      { id: generateId(), name: 'Crushed tomatoes', amount: 800, unit: 'g', notes: '2 cans' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Eggs', amount: 6, unit: 'piece' },
      { id: generateId(), name: 'Feta cheese', amount: 60, unit: 'g', notes: 'crumbled', optional: true },
      { id: generateId(), name: 'Fresh parsley or cilantro', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish' },
      { id: generateId(), name: 'Crusty bread', amount: 1, unit: 'piece', notes: 'for serving' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Heat olive oil in a large deep skillet or cast-iron pan over medium heat.' },
      { id: generateId(), order: 2, instruction: 'Add diced onion and bell pepper. Cook until softened and lightly golden, about 6 minutes.', timerDuration: 360, timerLabel: 'Soften veggies' },
      { id: generateId(), order: 3, instruction: 'Add garlic, cumin, paprika, smoked paprika, and cayenne. Stir for 1 minute until fragrant and spices toast.' },
      { id: generateId(), order: 4, instruction: 'Pour in crushed tomatoes. Season with salt and pepper. Stir and bring to a simmer.' },
      { id: generateId(), order: 5, instruction: 'Simmer sauce for 10 minutes until it thickens slightly and flavors meld.', timerDuration: 600, timerLabel: 'Sauce' },
      { id: generateId(), order: 6, instruction: 'Use a spoon to make 6 small wells in the sauce. Crack an egg into each well.' },
      { id: generateId(), order: 7, instruction: 'Cover the pan and cook on medium-low for 5-8 minutes until egg whites are set but yolks are still runny.', timerDuration: 420, timerLabel: 'Eggs' },
      { id: generateId(), order: 8, instruction: 'Scatter crumbled feta over the top if using. Sprinkle with fresh herbs. Serve straight from the pan with crusty bread for scooping.' },
    ],
    notes: 'Don\'t stir after adding the eggs. Cover tightly and resist peeking — steam cooks the tops of the eggs evenly. For firmer yolks, cook 1-2 minutes longer.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Greek Chicken Souvlaki',
    description: 'Juicy, herb-marinated grilled chicken skewers served with fluffy pita, cool tzatziki, and a bright tomato-onion salad. The taste of a Greek summer.',
    imageUrl: '/recipe-images/seed/greek-chicken-souvlaki.webp',
    prepTime: 20 * 60,
    cookTime: 12 * 60,
    totalTime: 32 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'greek',
    tags: ['grilled', 'healthy', 'summer', 'weeknight'],
    dietaryTags: ['gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chicken breast', amount: 700, unit: 'g', notes: 'cut into 1-inch cubes', group: 'Chicken' },
      { id: generateId(), name: 'Olive oil', amount: 3, unit: 'tbsp', group: 'Chicken' },
      { id: generateId(), name: 'Lemon juice', amount: 3, unit: 'tbsp', group: 'Chicken' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: 'minced', group: 'Chicken' },
      { id: generateId(), name: 'Dried oregano', amount: 1, unit: 'tbsp', group: 'Chicken' },
      { id: generateId(), name: 'Dried thyme', amount: 1, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Greek yogurt', amount: 1, unit: 'cup', notes: 'full-fat', group: 'Tzatziki' },
      { id: generateId(), name: 'Cucumber', amount: 0.5, unit: 'piece', notes: 'grated and squeezed dry', group: 'Tzatziki' },
      { id: generateId(), name: 'Garlic', amount: 1, unit: 'clove', notes: 'minced', group: 'Tzatziki' },
      { id: generateId(), name: 'Lemon juice', amount: 1, unit: 'tbsp', group: 'Tzatziki' },
      { id: generateId(), name: 'Fresh dill', amount: 1, unit: 'tbsp', notes: 'chopped', group: 'Tzatziki' },
      { id: generateId(), name: 'Salt', amount: 0.5, unit: 'tsp', group: 'Tzatziki' },
      { id: generateId(), name: 'Pita bread', amount: 4, unit: 'piece', notes: 'warmed' },
      { id: generateId(), name: 'Cherry tomatoes', amount: 200, unit: 'g', notes: 'halved' },
      { id: generateId(), name: 'Red onion', amount: 0.5, unit: 'piece', notes: 'thinly sliced' },
      { id: generateId(), name: 'Lemon wedges', amount: 4, unit: 'piece', notes: 'for serving' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Combine chicken cubes with olive oil, lemon juice, garlic, oregano, thyme, salt, and pepper. Toss to coat evenly. Marinate for at least 15 minutes.' },
      { id: generateId(), order: 2, instruction: 'Make tzatziki: mix Greek yogurt, grated cucumber (squeezed dry), garlic, lemon juice, dill, and salt. Refrigerate until serving.' },
      { id: generateId(), order: 3, instruction: 'Thread marinated chicken onto skewers (soak wooden skewers in water first to prevent burning).' },
      { id: generateId(), order: 4, instruction: 'Heat a grill pan or outdoor grill to medium-high heat. Brush grates with oil.' },
      { id: generateId(), order: 5, instruction: 'Grill chicken skewers for 5-6 minutes per side until charred and cooked through (internal temp 74°C/165°F).', timerDuration: 360, timerLabel: 'Grill side 1' },
      { id: generateId(), order: 6, instruction: 'Flip skewers and grill the other side for 5-6 minutes.', timerDuration: 360, timerLabel: 'Grill side 2' },
      { id: generateId(), order: 7, instruction: 'Warm pita bread on the grill for 30 seconds per side.' },
      { id: generateId(), order: 8, instruction: 'Serve skewers with warm pita, tzatziki, halved tomatoes, sliced red onion, and lemon wedges.' },
    ],
    notes: 'Soaking wooden skewers for 30 minutes prevents them from burning. If you have time, marinate the chicken for 2-4 hours in the fridge for maximum flavor.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Spanish Garlic Shrimp (Gambas al Ajillo)',
    description: 'Sizzling shrimp swimming in garlic-infused olive oil with a kick of chili — Spain\'s most iconic tapa. Ready in 10 minutes and impossibly delicious.',
    imageUrl: '/recipe-images/seed/spanish-garlic-shrimp-gambas-al-ajillo.webp',
    prepTime: 5 * 60,
    cookTime: 7 * 60,
    totalTime: 12 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'spanish',
    tags: ['seafood', 'quick', 'appetizer', 'tapas'],
    dietaryTags: ['gluten-free', 'dairy-free', 'pescatarian', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Large shrimp', amount: 500, unit: 'g', notes: 'peeled and deveined, tails on' },
      { id: generateId(), name: 'Extra virgin olive oil', amount: 0.33, unit: 'cup' },
      { id: generateId(), name: 'Garlic', amount: 8, unit: 'clove', notes: 'thinly sliced' },
      { id: generateId(), name: 'Dried red chili flakes', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Sweet paprika', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Fresh parsley', amount: 2, unit: 'tbsp', notes: 'finely chopped' },
      { id: generateId(), name: 'Lemon juice', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Crusty bread', amount: 1, unit: 'piece', notes: 'for soaking up the oil' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Pat shrimp dry with paper towels and season with salt and paprika.' },
      { id: generateId(), order: 2, instruction: 'Heat olive oil in a large skillet or cazuela over medium heat. Add sliced garlic and chili flakes.' },
      { id: generateId(), order: 3, instruction: 'Cook garlic, stirring constantly, until it just begins to turn golden, about 1-2 minutes. Do not let it brown.', timerDuration: 120, timerLabel: 'Garlic' },
      { id: generateId(), order: 4, instruction: 'Increase heat to high. Add shrimp in a single layer and cook without stirring for 1-2 minutes until pink on the bottom.', timerDuration: 120, timerLabel: 'Shrimp side 1' },
      { id: generateId(), order: 5, instruction: 'Flip shrimp and cook for 1 more minute until just cooked through and curled.', timerDuration: 60, timerLabel: 'Shrimp side 2' },
      { id: generateId(), order: 6, instruction: 'Remove from heat. Squeeze lemon juice over the top and scatter with fresh parsley.' },
      { id: generateId(), order: 7, instruction: 'Serve immediately straight from the pan, sizzling hot, with plenty of crusty bread for soaking up the garlic oil.' },
    ],
    notes: 'This dish cooks fast — have everything prepped and ready before you start. The garlic should be golden, never dark brown, or it will taste bitter. Serve with bread — the garlic oil is the best part.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Chicken Shawarma Rice Bowl',
    description: 'Warmly spiced, golden chicken shawarma over fluffy rice with pickled onions, crunchy vegetables, hummus, and a drizzle of creamy tahini. Meal prep perfection.',
    imageUrl: '/recipe-images/seed/chicken-shawarma-rice-bowl.webp',
    prepTime: 15 * 60,
    cookTime: 15 * 60,
    totalTime: 30 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'middle-eastern',
    tags: ['bowl', 'meal-prep', 'healthy', 'weeknight'],
    dietaryTags: ['dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chicken thighs', amount: 700, unit: 'g', notes: 'boneless, skinless', group: 'Chicken' },
      { id: generateId(), name: 'Olive oil', amount: 2, unit: 'tbsp', group: 'Chicken' },
      { id: generateId(), name: 'Cumin', amount: 1.5, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Paprika', amount: 1, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Turmeric', amount: 0.5, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Cinnamon', amount: 0.25, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Garlic powder', amount: 1, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Lemon juice', amount: 2, unit: 'tbsp', group: 'Chicken' },
      { id: generateId(), name: 'Basmati rice', amount: 1.5, unit: 'cup', notes: 'rinsed' },
      { id: generateId(), name: 'Red onion', amount: 1, unit: 'piece', notes: 'thinly sliced', group: 'Pickled Onion' },
      { id: generateId(), name: 'Red wine vinegar', amount: 3, unit: 'tbsp', group: 'Pickled Onion' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tsp', group: 'Pickled Onion' },
      { id: generateId(), name: 'Cucumber', amount: 1, unit: 'piece', notes: 'diced', group: 'Bowl' },
      { id: generateId(), name: 'Cherry tomatoes', amount: 200, unit: 'g', notes: 'halved', group: 'Bowl' },
      { id: generateId(), name: 'Hummus', amount: 0.5, unit: 'cup', group: 'Bowl' },
      { id: generateId(), name: 'Tahini', amount: 3, unit: 'tbsp', notes: 'thinned with 1 tbsp water', group: 'Bowl' },
      { id: generateId(), name: 'Fresh parsley', amount: 0.25, unit: 'cup', notes: 'chopped', group: 'Bowl' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Quick-pickle onions: toss sliced red onion with vinegar and sugar in a small bowl. Set aside while you cook — they\'ll soften and turn pink.' },
      { id: generateId(), order: 2, instruction: 'Combine chicken with olive oil, cumin, paprika, turmeric, cinnamon, garlic powder, salt, and lemon juice. Mix well to coat.' },
      { id: generateId(), order: 3, instruction: 'Cook rice according to package directions. Keep warm.' },
      { id: generateId(), order: 4, instruction: 'Heat a large skillet or grill pan over medium-high heat. Add chicken thighs and cook for 6-7 minutes per side until deeply golden and cooked through (internal temp 74°C/165°F).', timerDuration: 420, timerLabel: 'Chicken side 1' },
      { id: generateId(), order: 5, instruction: 'Flip chicken and cook the second side for 6-7 minutes.', timerDuration: 420, timerLabel: 'Chicken side 2' },
      { id: generateId(), order: 6, instruction: 'Rest chicken for 3 minutes, then slice against the grain into strips.', timerDuration: 180, timerLabel: 'Rest' },
      { id: generateId(), order: 7, instruction: 'Assemble bowls: divide rice among 4 bowls. Top with sliced chicken, diced cucumber, halved tomatoes, and drained pickled onions.' },
      { id: generateId(), order: 8, instruction: 'Add a dollop of hummus, drizzle with thinned tahini, and sprinkle with fresh parsley. Serve with lemon wedges.' },
    ],
    notes: 'This recipe is excellent for meal prep — cook the chicken and rice in bulk, store components separately, and assemble fresh bowls throughout the week. The pickled onions keep for up to a week in the fridge.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Chocolate Lava Cake',
    description: 'A decadent individual chocolate cake with a warm, molten center that flows like lava when you break through the crust. Easier than it looks, impossibly elegant.',
    imageUrl: '/recipe-images/seed/chocolate-lava-cake.webp',
    prepTime: 15 * 60,
    cookTime: 14 * 60,
    totalTime: 29 * 60,
    servings: 4,
    difficulty: 'hard',
    cuisine: 'french',
    tags: ['dessert', 'chocolate', 'date-night', 'baked'],
    dietaryTags: ['vegetarian', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Dark chocolate', amount: 170, unit: 'g', notes: '70% cocoa, finely chopped' },
      { id: generateId(), name: 'Unsalted butter', amount: 115, unit: 'g', notes: 'plus extra for greasing' },
      { id: generateId(), name: 'Eggs', amount: 2, unit: 'piece', notes: 'room temperature' },
      { id: generateId(), name: 'Egg yolks', amount: 2, unit: 'piece', notes: 'room temperature' },
      { id: generateId(), name: 'Granulated sugar', amount: 60, unit: 'g' },
      { id: generateId(), name: 'All-purpose flour', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'pinch' },
      { id: generateId(), name: 'Cocoa powder', amount: 1, unit: 'tbsp', notes: 'for dusting ramekins' },
      { id: generateId(), name: 'Vanilla extract', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Powdered sugar', amount: 1, unit: 'tbsp', notes: 'for dusting', optional: true },
      { id: generateId(), name: 'Vanilla ice cream', amount: 4, unit: 'piece', notes: 'scoops, for serving', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Preheat oven to 220°C (425°F). Butter four 6-oz ramekins generously, then dust with cocoa powder, tapping out excess.' },
      { id: generateId(), order: 2, instruction: 'Melt chocolate and butter together in a heatproof bowl over simmering water (or microwave in 30-second bursts), stirring until smooth. Let cool slightly.' },
      { id: generateId(), order: 3, instruction: 'In a separate bowl, whisk eggs, egg yolks, and sugar vigorously until thick and pale, about 2 minutes.' },
      { id: generateId(), order: 4, instruction: 'Pour the melted chocolate mixture into the egg mixture. Add vanilla extract and fold gently with a spatula until just combined.' },
      { id: generateId(), order: 5, instruction: 'Sift flour and salt over the batter. Fold gently until no white streaks remain — do not overmix.' },
      { id: generateId(), order: 6, instruction: 'Divide batter evenly among prepared ramekins, filling about three-quarters full. Place ramekins on a baking sheet.' },
      { id: generateId(), order: 7, instruction: 'Bake for 12-14 minutes until the edges are firm and set but the center still jiggles slightly when shaken.', timerDuration: 780, timerLabel: 'Bake' },
      { id: generateId(), order: 8, instruction: 'Let rest for 1 minute only. Run a knife around the edge, place a plate on top, and flip. Tap gently and lift the ramekin.', timerDuration: 60, timerLabel: 'Rest' },
      { id: generateId(), order: 9, instruction: 'Dust with powdered sugar and serve immediately with a scoop of vanilla ice cream. The center should flow when cut open.' },
    ],
    notes: 'Timing is everything — 1 minute too long and you get a brownie; 1 minute too short and it collapses. Test with one ramekin first. Batter can be made ahead and refrigerated (add 2 minutes to bake time if cold).',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Chicken Biryani',
    description: 'Fragrant layers of spiced basmati rice and tender marinated chicken, slow-cooked together until every grain is infused with saffron, cardamom, and caramelized onions.',
    imageUrl: '/recipe-images/seed/chicken-biryani.webp',
    prepTime: 30 * 60,
    cookTime: 40 * 60,
    totalTime: 70 * 60,
    servings: 6,
    difficulty: 'medium',
    cuisine: 'indian',
    tags: ['rice', 'dinner', 'classic', 'one-pot'],
    dietaryTags: ['gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chicken thighs', amount: 800, unit: 'g', notes: 'bone-in, skinless', group: 'Chicken' },
      { id: generateId(), name: 'Plain yogurt', amount: 1, unit: 'cup', group: 'Chicken' },
      { id: generateId(), name: 'Ginger-garlic paste', amount: 2, unit: 'tbsp', group: 'Chicken' },
      { id: generateId(), name: 'Biryani masala', amount: 2, unit: 'tbsp', notes: 'or garam masala', group: 'Chicken' },
      { id: generateId(), name: 'Turmeric', amount: 0.5, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Red chili powder', amount: 1, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Salt', amount: 1.5, unit: 'tsp', group: 'Chicken' },
      { id: generateId(), name: 'Lemon juice', amount: 2, unit: 'tbsp', group: 'Chicken' },
      { id: generateId(), name: 'Basmati rice', amount: 2.5, unit: 'cup', notes: 'soaked 30 min and drained', group: 'Rice' },
      { id: generateId(), name: 'Water', amount: 8, unit: 'cup', notes: 'for parboiling rice', group: 'Rice' },
      { id: generateId(), name: 'Whole spices', amount: 1, unit: 'tsp', notes: 'cardamom, cloves, bay leaf, cinnamon stick', group: 'Rice' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tbsp', notes: 'for rice water', group: 'Rice' },
      { id: generateId(), name: 'Onions', amount: 3, unit: 'piece', notes: 'thinly sliced', group: 'Layering' },
      { id: generateId(), name: 'Vegetable oil', amount: 4, unit: 'tbsp', group: 'Layering' },
      { id: generateId(), name: 'Saffron', amount: 1, unit: 'pinch', notes: 'soaked in 3 tbsp warm milk', group: 'Layering' },
      { id: generateId(), name: 'Fresh mint', amount: 0.5, unit: 'cup', notes: 'leaves only', group: 'Layering' },
      { id: generateId(), name: 'Fresh cilantro', amount: 0.5, unit: 'cup', notes: 'chopped', group: 'Layering' },
      { id: generateId(), name: 'Ghee', amount: 2, unit: 'tbsp', group: 'Layering' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Marinate chicken with yogurt, ginger-garlic paste, biryani masala, turmeric, chili powder, salt, and lemon juice. Refrigerate for at least 30 minutes.' },
      { id: generateId(), order: 2, instruction: 'Fry onions: heat oil in a deep heavy pot over medium-high heat. Fry sliced onions until deep golden-brown and crispy, about 12 minutes. Remove half and set aside for garnish.', timerDuration: 720, timerLabel: 'Fry onions' },
      { id: generateId(), order: 3, instruction: 'Add marinated chicken on top of the remaining fried onions in the pot. Spread evenly. Cook on medium heat for 10 minutes, covered.', timerDuration: 600, timerLabel: 'Cook chicken' },
      { id: generateId(), order: 4, instruction: 'While chicken cooks, parboil rice: bring 8 cups water to a boil with whole spices and salt. Add drained rice and cook until 70% done (grain should still have a bite), about 5 minutes. Drain.', timerDuration: 300, timerLabel: 'Parboil rice' },
      { id: generateId(), order: 5, instruction: 'Layer parboiled rice over the chicken. Scatter mint, cilantro, reserved fried onions, and drizzle saffron milk over the top. Dot with ghee.' },
      { id: generateId(), order: 6, instruction: 'Seal the pot tightly with foil, then place the lid on top. Cook on the lowest heat for 25 minutes — do not open or stir.', timerDuration: 1500, timerLabel: 'Dum cook' },
      { id: generateId(), order: 7, instruction: 'Remove from heat and let rest for 5 minutes, still sealed.', timerDuration: 300, timerLabel: 'Rest' },
      { id: generateId(), order: 8, instruction: 'Open the pot and gently fold the layers together with a wide spoon, being careful not to break the rice grains. Serve with raita.' },
    ],
    notes: 'The dum (steam-cooking) step is where the magic happens — resist opening the lid. If you smell the aroma intensifying, the heat is right. Soaking the rice for 30 minutes is essential for long, separate grains.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Pad Thai',
    description: 'Thailand\'s most famous noodle dish — sweet, sour, salty, and savory all at once. Chewy rice noodles tossed with shrimp, egg, crunchy peanuts, and a tamarind sauce.',
    imageUrl: '/recipe-images/seed/pad-thai.webp',
    prepTime: 20 * 60,
    cookTime: 10 * 60,
    totalTime: 30 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'thai',
    tags: ['noodles', 'quick', 'classic', 'stir-fry'],
    dietaryTags: ['dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Rice noodles', amount: 250, unit: 'g', notes: 'flat, dried, medium width' },
      { id: generateId(), name: 'Large shrimp', amount: 300, unit: 'g', notes: 'peeled and deveined' },
      { id: generateId(), name: 'Eggs', amount: 2, unit: 'piece' },
      { id: generateId(), name: 'Extra-firm tofu', amount: 150, unit: 'g', notes: 'pressed and cubed', optional: true },
      { id: generateId(), name: 'Bean sprouts', amount: 150, unit: 'g' },
      { id: generateId(), name: 'Green onions', amount: 4, unit: 'piece', notes: 'cut into 2-inch pieces' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Vegetable oil', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Tamarind paste', amount: 3, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Fish sauce', amount: 3, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Sugar', amount: 2, unit: 'tbsp', notes: 'palm sugar or brown sugar', group: 'Sauce' },
      { id: generateId(), name: 'Lime juice', amount: 1, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Roasted peanuts', amount: 0.33, unit: 'cup', notes: 'roughly chopped', group: 'Garnish' },
      { id: generateId(), name: 'Lime wedges', amount: 4, unit: 'piece', group: 'Garnish' },
      { id: generateId(), name: 'Fresh cilantro', amount: 2, unit: 'tbsp', notes: 'chopped', group: 'Garnish' },
      { id: generateId(), name: 'Dried red chili flakes', amount: 1, unit: 'tsp', group: 'Garnish', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Soak rice noodles in warm water for 15-20 minutes until pliable but still firm. Drain well.', timerDuration: 1200, timerLabel: 'Soak noodles' },
      { id: generateId(), order: 2, instruction: 'Mix sauce: whisk together tamarind paste, fish sauce, sugar, and lime juice until sugar dissolves.' },
      { id: generateId(), order: 3, instruction: 'Heat 1 tbsp oil in a wok over high heat. Add tofu if using and fry until golden on all sides, about 3 minutes. Remove and set aside.', timerDuration: 180, timerLabel: 'Tofu' },
      { id: generateId(), order: 4, instruction: 'Add another 1 tbsp oil. Cook shrimp for 2 minutes until pink and just curled. Remove and set aside.', timerDuration: 120, timerLabel: 'Shrimp' },
      { id: generateId(), order: 5, instruction: 'Add remaining oil and garlic. Stir for 15 seconds. Push to the side and crack eggs into the wok. Scramble quickly until just set.' },
      { id: generateId(), order: 6, instruction: 'Add drained noodles and sauce to the wok. Toss with tongs over high heat for 2 minutes until noodles absorb the sauce and soften.', timerDuration: 120, timerLabel: 'Noodles' },
      { id: generateId(), order: 7, instruction: 'Return shrimp and tofu. Add bean sprouts and green onions. Toss everything together for 30 seconds.' },
      { id: generateId(), order: 8, instruction: 'Serve immediately topped with chopped peanuts, lime wedges, cilantro, and chili flakes.' },
    ],
    notes: 'The wok must be screaming hot — pad thai should take under 5 minutes of actual cooking. If your noodles clump, sprinkle with a tablespoon of water while tossing. Don\'t overcook the shrimp.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Crispy Falafel Wrap',
    description: 'Golden, herb-packed falafel tucked into warm pita with crunchy vegetables, pickled turnips, and a creamy tahini drizzle. Street food at its finest.',
    imageUrl: '/recipe-images/seed/crispy-falafel-wrap.webp',
    prepTime: 25 * 60,
    cookTime: 20 * 60,
    totalTime: 45 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'middle-eastern',
    tags: ['street food', 'lunch', 'healthy', 'classic'],
    dietaryTags: ['vegan', 'vegetarian', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Dried chickpeas', amount: 300, unit: 'g', notes: 'soaked overnight, drained (do NOT use canned)', group: 'Falafel' },
      { id: generateId(), name: 'Fresh parsley', amount: 1, unit: 'cup', notes: 'packed', group: 'Falafel' },
      { id: generateId(), name: 'Fresh cilantro', amount: 0.5, unit: 'cup', notes: 'packed', group: 'Falafel' },
      { id: generateId(), name: 'Onion', amount: 0.5, unit: 'piece', notes: 'roughly chopped', group: 'Falafel' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', group: 'Falafel' },
      { id: generateId(), name: 'Cumin', amount: 1.5, unit: 'tsp', group: 'Falafel' },
      { id: generateId(), name: 'Coriander', amount: 1, unit: 'tsp', notes: 'ground', group: 'Falafel' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp', group: 'Falafel' },
      { id: generateId(), name: 'Baking powder', amount: 0.5, unit: 'tsp', group: 'Falafel' },
      { id: generateId(), name: 'Vegetable oil', amount: 1, unit: 'cup', notes: 'for shallow frying', group: 'Falafel' },
      { id: generateId(), name: 'Tahini', amount: 0.33, unit: 'cup', group: 'Tahini Sauce' },
      { id: generateId(), name: 'Lemon juice', amount: 2, unit: 'tbsp', group: 'Tahini Sauce' },
      { id: generateId(), name: 'Water', amount: 3, unit: 'tbsp', notes: 'to thin', group: 'Tahini Sauce' },
      { id: generateId(), name: 'Garlic', amount: 1, unit: 'clove', notes: 'minced', group: 'Tahini Sauce' },
      { id: generateId(), name: 'Salt', amount: 0.25, unit: 'tsp', group: 'Tahini Sauce' },
      { id: generateId(), name: 'Pita bread', amount: 4, unit: 'piece', notes: 'large, warmed', group: 'Assembly' },
      { id: generateId(), name: 'Lettuce', amount: 4, unit: 'piece', notes: 'leaves', group: 'Assembly' },
      { id: generateId(), name: 'Tomato', amount: 2, unit: 'piece', notes: 'sliced', group: 'Assembly' },
      { id: generateId(), name: 'Pickled turnips', amount: 0.5, unit: 'cup', notes: 'or pickled cucumbers', group: 'Assembly', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Add soaked chickpeas, parsley, cilantro, onion, garlic, cumin, coriander, and salt to a food processor. Pulse until a coarse, crumbly mixture forms — do not over-process into a paste.' },
      { id: generateId(), order: 2, instruction: 'Transfer to a bowl, cover, and refrigerate for 30 minutes to firm up. This helps the falafel hold together.', timerDuration: 1800, timerLabel: 'Chill' },
      { id: generateId(), order: 3, instruction: 'Make tahini sauce: whisk tahini, lemon juice, water, garlic, and salt until smooth and pourable. Add more water if needed.' },
      { id: generateId(), order: 4, instruction: 'Mix baking powder into the falafel mixture. Shape into 16 small patties (about 2 inches wide, slightly flattened).' },
      { id: generateId(), order: 5, instruction: 'Heat oil in a deep skillet to 175°C (350°F), about 1 inch deep. The oil is ready when a small piece of mixture sizzles immediately.' },
      { id: generateId(), order: 6, instruction: 'Fry falafel in batches of 4-5, turning once, until deep golden-brown on all sides, about 3-4 minutes total per batch.', timerDuration: 240, timerLabel: 'Fry falafel' },
      { id: generateId(), order: 7, instruction: 'Drain on paper towels. Keep warm while frying remaining batches.' },
      { id: generateId(), order: 8, instruction: 'Assemble wraps: lay warm pita flat, add lettuce, 3-4 falafel, tomato slices, pickled turnips, and a generous drizzle of tahini sauce. Roll tightly and serve.' },
    ],
    notes: 'You MUST use dried chickpeas soaked overnight — canned chickpeas have too much moisture and the falafel will fall apart. If mixture is too wet, add 1-2 tbsp flour. Falafel can be frozen uncooked and fried directly from frozen.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Creamy Mushroom Risotto',
    description: 'Luxuriously creamy Italian rice slowly cooked with a mix of mushrooms, finished with Parmesan and fresh thyme. Patient stirring rewards you with silk-smooth perfection.',
    imageUrl: '/recipe-images/seed/creamy-mushroom-risotto.webp',
    prepTime: 10 * 60,
    cookTime: 30 * 60,
    totalTime: 40 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'italian',
    tags: ['rice', 'comfort food', 'dinner', 'vegetarian'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Arborio rice', amount: 300, unit: 'g' },
      { id: generateId(), name: 'Mixed mushrooms', amount: 400, unit: 'g', notes: 'cremini, shiitake, and oyster, sliced' },
      { id: generateId(), name: 'Vegetable broth', amount: 1, unit: 'l', notes: 'warm, kept simmering' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'finely diced' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Unsalted butter', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Olive oil', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Parmesan cheese', amount: 60, unit: 'g', notes: 'finely grated' },
      { id: generateId(), name: 'Fresh thyme', amount: 1, unit: 'tbsp', notes: 'leaves only' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'to taste' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Fresh parsley', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Heat olive oil and 1 tbsp butter in a large heavy-bottomed pan over medium-high heat. Add mushrooms and cook until golden and moisture evaporates, about 6 minutes. Season with salt. Remove and set aside.', timerDuration: 360, timerLabel: 'Mushrooms' },
      { id: generateId(), order: 2, instruction: 'In the same pan, melt 1 tbsp butter. Add diced onion and cook until translucent, about 4 minutes.' },
      { id: generateId(), order: 3, instruction: 'Add garlic and thyme. Stir for 30 seconds until fragrant.' },
      { id: generateId(), order: 4, instruction: 'Add arborio rice and stir for 2 minutes until the grains are coated and slightly translucent at the edges.', timerDuration: 120, timerLabel: 'Toast rice' },
      { id: generateId(), order: 5, instruction: 'Add a ladleful of warm broth (about 120ml). Stir frequently until the liquid is almost fully absorbed. Repeat, one ladle at a time, for about 18-20 minutes until rice is creamy but still has a slight bite.', timerDuration: 1200, timerLabel: 'Risotto' },
      { id: generateId(), order: 6, instruction: 'Return mushrooms to the pan. Stir in remaining 1 tbsp cold butter and the Parmesan. Stir vigorously for 30 seconds — this creates the signature creaminess.' },
      { id: generateId(), order: 7, instruction: 'Remove from heat. Season with salt and pepper. The risotto should flow like lava, not sit in a mound — add a splash more broth if needed.' },
      { id: generateId(), order: 8, instruction: 'Serve immediately in warm bowls, garnished with extra Parmesan, fresh parsley, and cracked pepper.' },
    ],
    notes: 'Never stop stirring during the broth addition phase — constant stirring releases starch and creates creaminess. Always add warm broth, never cold, or it will shock the rice and slow cooking.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Teriyaki Chicken Bowl',
    description: 'Glossy, caramelized teriyaki chicken over steamed rice with edamame, pickled ginger, and a sprinkle of sesame seeds. A Japanese weeknight classic done right.',
    imageUrl: '/recipe-images/seed/teriyaki-chicken-bowl.webp',
    prepTime: 10 * 60,
    cookTime: 15 * 60,
    totalTime: 25 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'japanese',
    tags: ['bowl', 'quick', 'weeknight', 'family-friendly'],
    dietaryTags: ['dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chicken thighs', amount: 600, unit: 'g', notes: 'boneless, skinless' },
      { id: generateId(), name: 'Soy sauce', amount: 4, unit: 'tbsp', group: 'Teriyaki Sauce' },
      { id: generateId(), name: 'Rice vinegar', amount: 2, unit: 'tbsp', group: 'Teriyaki Sauce' },
      { id: generateId(), name: 'Honey', amount: 3, unit: 'tbsp', group: 'Teriyaki Sauce' },
      { id: generateId(), name: 'Garlic', amount: 2, unit: 'clove', notes: 'minced', group: 'Teriyaki Sauce' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'tsp', notes: 'freshly grated', group: 'Teriyaki Sauce' },
      { id: generateId(), name: 'Cornstarch', amount: 1, unit: 'tsp', notes: 'mixed with 1 tbsp water', group: 'Teriyaki Sauce' },
      { id: generateId(), name: 'Vegetable oil', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Short-grain rice', amount: 2, unit: 'cup', notes: 'cooked' },
      { id: generateId(), name: 'Edamame', amount: 150, unit: 'g', notes: 'shelled, steamed', group: 'Toppings' },
      { id: generateId(), name: 'Cucumber', amount: 1, unit: 'piece', notes: 'sliced into ribbons', group: 'Toppings' },
      { id: generateId(), name: 'Pickled ginger', amount: 2, unit: 'tbsp', group: 'Toppings', optional: true },
      { id: generateId(), name: 'Sesame seeds', amount: 1, unit: 'tbsp', group: 'Toppings' },
      { id: generateId(), name: 'Green onions', amount: 2, unit: 'piece', notes: 'sliced', group: 'Toppings' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Mix teriyaki sauce: combine soy sauce, rice vinegar, honey, garlic, and ginger in a small bowl.' },
      { id: generateId(), order: 2, instruction: 'Heat oil in a large skillet over medium-high heat. Add chicken thighs and cook for 5-6 minutes per side until golden and cooked through (internal temp 74°C/165°F).', timerDuration: 360, timerLabel: 'Chicken side 1' },
      { id: generateId(), order: 3, instruction: 'Flip chicken and cook the second side for 5-6 minutes.', timerDuration: 360, timerLabel: 'Chicken side 2' },
      { id: generateId(), order: 4, instruction: 'Pour teriyaki sauce into the pan. Let it bubble and reduce for 2 minutes, spooning sauce over the chicken as it thickens.', timerDuration: 120, timerLabel: 'Glaze' },
      { id: generateId(), order: 5, instruction: 'Add cornstarch slurry to the sauce. Stir until glossy and thickened, about 30 seconds.' },
      { id: generateId(), order: 6, instruction: 'Remove chicken and let rest for 2 minutes. Slice against the grain into thick strips.' },
      { id: generateId(), order: 7, instruction: 'Divide steamed rice among 4 bowls. Arrange sliced chicken on top, drizzling with pan sauce.' },
      { id: generateId(), order: 8, instruction: 'Add edamame, cucumber ribbons, pickled ginger, sesame seeds, and sliced green onions. Serve with remaining sauce on the side.' },
    ],
    notes: 'Let the sauce reduce until it coats the back of a spoon — that glossy thickness is the hallmark of good teriyaki. This sauce uses rice vinegar instead of mirin, keeping it halal-friendly.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Lamb Kofta Kebabs',
    description: 'Juicy, warmly spiced ground lamb kebabs grilled on skewers and served with fluffy flatbread, tangy yogurt sauce, and a bright herb salad.',
    imageUrl: '/recipe-images/seed/lamb-kofta-kebabs.webp',
    prepTime: 20 * 60,
    cookTime: 10 * 60,
    totalTime: 30 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'middle-eastern',
    tags: ['grilled', 'dinner', 'family-friendly', 'summer'],
    dietaryTags: ['gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Ground lamb', amount: 600, unit: 'g', group: 'Kofta' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'grated and squeezed dry', group: 'Kofta' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: 'minced', group: 'Kofta' },
      { id: generateId(), name: 'Fresh parsley', amount: 0.25, unit: 'cup', notes: 'finely chopped', group: 'Kofta' },
      { id: generateId(), name: 'Cumin', amount: 1.5, unit: 'tsp', group: 'Kofta' },
      { id: generateId(), name: 'Coriander', amount: 1, unit: 'tsp', notes: 'ground', group: 'Kofta' },
      { id: generateId(), name: 'Paprika', amount: 1, unit: 'tsp', group: 'Kofta' },
      { id: generateId(), name: 'Cinnamon', amount: 0.25, unit: 'tsp', group: 'Kofta' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp', group: 'Kofta' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp', group: 'Kofta' },
      { id: generateId(), name: 'Greek yogurt', amount: 0.5, unit: 'cup', group: 'Yogurt Sauce' },
      { id: generateId(), name: 'Lemon juice', amount: 1, unit: 'tbsp', group: 'Yogurt Sauce' },
      { id: generateId(), name: 'Fresh mint', amount: 1, unit: 'tbsp', notes: 'finely chopped', group: 'Yogurt Sauce' },
      { id: generateId(), name: 'Salt', amount: 0.25, unit: 'tsp', group: 'Yogurt Sauce' },
      { id: generateId(), name: 'Flatbread or pita', amount: 4, unit: 'piece', notes: 'warmed' },
      { id: generateId(), name: 'Cherry tomatoes', amount: 200, unit: 'g', notes: 'halved' },
      { id: generateId(), name: 'Red onion', amount: 0.5, unit: 'piece', notes: 'thinly sliced' },
      { id: generateId(), name: 'Fresh mint and parsley', amount: 0.5, unit: 'cup', notes: 'mixed, for salad' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Combine ground lamb, grated onion (squeezed dry), garlic, parsley, cumin, coriander, paprika, cinnamon, salt, and pepper in a bowl. Mix well with your hands until evenly combined.' },
      { id: generateId(), order: 2, instruction: 'Divide into 8 portions. Shape each around a flat metal skewer into a long oval (about 5 inches). If using wooden skewers, soak for 30 minutes first.' },
      { id: generateId(), order: 3, instruction: 'Make yogurt sauce: mix Greek yogurt, lemon juice, mint, and salt. Refrigerate until serving.' },
      { id: generateId(), order: 4, instruction: 'Heat a grill pan or outdoor grill to high heat. Brush grates with oil.' },
      { id: generateId(), order: 5, instruction: 'Grill kofta for 4-5 minutes per side until charred on the outside and cooked through (no pink remaining).', timerDuration: 300, timerLabel: 'Grill side 1' },
      { id: generateId(), order: 6, instruction: 'Flip and grill the second side for 4-5 minutes.', timerDuration: 300, timerLabel: 'Grill side 2' },
      { id: generateId(), order: 7, instruction: 'Warm flatbread on the grill for 20 seconds per side.' },
      { id: generateId(), order: 8, instruction: 'Serve kofta on warm flatbread with yogurt sauce, halved tomatoes, sliced red onion, and fresh herb salad.' },
    ],
    notes: 'Squeezing the grated onion dry is essential — excess moisture makes the kofta fall apart on the grill. For extra juiciness, use lamb with at least 15-20% fat content.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Fluffy Banana Pancakes',
    description: 'Thick, fluffy pancakes studded with caramelized banana slices and a hint of cinnamon. Drizzle with maple syrup for the ultimate lazy weekend breakfast.',
    imageUrl: '/recipe-images/seed/fluffy-banana-pancakes.webp',
    prepTime: 10 * 60,
    cookTime: 15 * 60,
    totalTime: 25 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'american',
    tags: ['breakfast', 'quick', 'family-friendly', 'sweet'],
    dietaryTags: ['vegetarian', 'halal'],
    ingredients: [
      { id: generateId(), name: 'All-purpose flour', amount: 1.5, unit: 'cup' },
      { id: generateId(), name: 'Baking powder', amount: 2, unit: 'tsp' },
      { id: generateId(), name: 'Cinnamon', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 0.25, unit: 'tsp' },
      { id: generateId(), name: 'Sugar', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Egg', amount: 1, unit: 'piece' },
      { id: generateId(), name: 'Milk', amount: 1, unit: 'cup' },
      { id: generateId(), name: 'Unsalted butter', amount: 2, unit: 'tbsp', notes: 'melted, plus extra for cooking' },
      { id: generateId(), name: 'Vanilla extract', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Ripe bananas', amount: 2, unit: 'piece', notes: '1 mashed, 1 sliced for topping' },
      { id: generateId(), name: 'Maple syrup', amount: 4, unit: 'tbsp', notes: 'for serving' },
      { id: generateId(), name: 'Fresh berries', amount: 0.5, unit: 'cup', notes: 'for topping', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Whisk together flour, baking powder, cinnamon, salt, and sugar in a large bowl.' },
      { id: generateId(), order: 2, instruction: 'In a separate bowl, beat egg, then add milk, melted butter, vanilla, and mashed banana. Whisk until combined.' },
      { id: generateId(), order: 3, instruction: 'Pour wet ingredients into dry ingredients. Stir gently until just combined — lumps are OK and actually desirable. Do not overmix or pancakes will be tough.' },
      { id: generateId(), order: 4, instruction: 'Heat a non-stick pan or griddle over medium heat. Add a small pat of butter.' },
      { id: generateId(), order: 5, instruction: 'Pour about 1/3 cup batter per pancake. Place a few banana slices on top. Cook until bubbles form on the surface and edges look set, about 2-3 minutes.', timerDuration: 180, timerLabel: 'Side 1' },
      { id: generateId(), order: 6, instruction: 'Flip carefully and cook the other side for 1-2 minutes until golden brown.', timerDuration: 120, timerLabel: 'Side 2' },
      { id: generateId(), order: 7, instruction: 'Repeat with remaining batter, adjusting heat as needed. Keep finished pancakes warm in a 90°C (200°F) oven.' },
      { id: generateId(), order: 8, instruction: 'Stack pancakes on plates. Top with remaining banana slices, berries if using, a pat of butter, and a generous drizzle of maple syrup.' },
    ],
    notes: 'The secret to fluffy pancakes is a lumpy batter — overmixing develops gluten and makes them flat and chewy. Let the batter rest for 5 minutes before cooking for even fluffier results.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  // ── French ──────────────────────────────────────────────────────────
  {
    id: generateId(),
    name: 'French Onion Soup',
    description: 'A deeply caramelized onion soup topped with crusty bread and melted Gruyère cheese. Rich, savory, and soul-warming — the ultimate French comfort food.',
    imageUrl: '/recipe-images/seed/french-onion-soup.webp',
    prepTime: 15 * 60,
    cookTime: 60 * 60,
    totalTime: 75 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'french',
    tags: ['soup', 'comfort food', 'classic', 'winter'],
    dietaryTags: ['halal'],
    ingredients: [
      { id: generateId(), name: 'Yellow onions', amount: 6, unit: 'piece', notes: 'thinly sliced' },
      { id: generateId(), name: 'Unsalted butter', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Olive oil', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tsp', notes: 'to help caramelization' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'All-purpose flour', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Beef broth', amount: 1.5, unit: 'l', notes: 'halal certified' },
      { id: generateId(), name: 'Fresh thyme', amount: 3, unit: 'piece', notes: 'sprigs' },
      { id: generateId(), name: 'Bay leaf', amount: 1, unit: 'piece' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Baguette', amount: 8, unit: 'slice', notes: '1.5cm thick, day-old preferred', group: 'Topping' },
      { id: generateId(), name: 'Gruyère cheese', amount: 200, unit: 'g', notes: 'grated', group: 'Topping' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Slice all onions thinly — take your time, even slices caramelize evenly.' },
      { id: generateId(), order: 2, instruction: 'Melt butter with olive oil in a large heavy pot over medium heat. Add all sliced onions and salt. Stir to coat.' },
      { id: generateId(), order: 3, instruction: 'Cook onions for 35-40 minutes, stirring every 5 minutes, until deeply golden and jammy. Sprinkle sugar after 20 minutes to help caramelization.', timerDuration: 2400, timerLabel: 'Caramelize' },
      { id: generateId(), order: 4, instruction: 'Sprinkle flour over the onions and stir for 1 minute to cook out the raw taste.' },
      { id: generateId(), order: 5, instruction: 'Add beef broth, thyme sprigs, and bay leaf. Bring to a simmer and cook for 15 minutes.', timerDuration: 900, timerLabel: 'Simmer' },
      { id: generateId(), order: 6, instruction: 'Remove thyme sprigs and bay leaf. Season with pepper. Taste and adjust salt.' },
      { id: generateId(), order: 7, instruction: 'Preheat broiler. Ladle soup into oven-safe bowls. Place baguette slices on top and pile generously with grated Gruyère.' },
      { id: generateId(), order: 8, instruction: 'Broil for 3-4 minutes until cheese is bubbly and golden brown. Watch closely to avoid burning.', timerDuration: 240, timerLabel: 'Broil' },
      { id: generateId(), order: 9, instruction: 'Let cool for 2 minutes — the bowls will be extremely hot. Serve immediately.' },
    ],
    notes: 'Patience with the onions is everything. Don\'t rush the caramelization — low and slow builds the deep sweetness. If onions start to stick, add a splash of water and scrape the browned bits (fond) back in.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Ratatouille',
    description: 'A beautiful Provençal vegetable stew layered with zucchini, eggplant, tomatoes, and bell peppers in a fragrant herb-tomato sauce. Rustic, colorful, and deeply satisfying.',
    imageUrl: '/recipe-images/seed/ratatouille.webp',
    prepTime: 25 * 60,
    cookTime: 45 * 60,
    totalTime: 70 * 60,
    servings: 6,
    difficulty: 'easy',
    cuisine: 'french',
    tags: ['vegetable', 'classic', 'healthy', 'comfort food'],
    dietaryTags: ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Zucchini', amount: 2, unit: 'piece', notes: 'sliced into 3mm rounds' },
      { id: generateId(), name: 'Eggplant', amount: 1, unit: 'piece', notes: 'sliced into 3mm rounds' },
      { id: generateId(), name: 'Yellow squash', amount: 1, unit: 'piece', notes: 'sliced into 3mm rounds' },
      { id: generateId(), name: 'Roma tomatoes', amount: 3, unit: 'piece', notes: 'sliced into 3mm rounds' },
      { id: generateId(), name: 'Olive oil', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Fresh thyme', amount: 1, unit: 'tbsp', notes: 'leaves only' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'diced', group: 'Sauce' },
      { id: generateId(), name: 'Red bell pepper', amount: 1, unit: 'piece', notes: 'diced', group: 'Sauce' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced', group: 'Sauce' },
      { id: generateId(), name: 'Crushed tomatoes', amount: 400, unit: 'g', notes: 'canned', group: 'Sauce' },
      { id: generateId(), name: 'Tomato paste', amount: 2, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Fresh basil', amount: 0.25, unit: 'cup', notes: 'torn, for garnish', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Preheat oven to 190°C (375°F).' },
      { id: generateId(), order: 2, instruction: 'Make the sauce: heat 1 tbsp olive oil in an oven-safe skillet over medium heat. Sauté onion and bell pepper until soft, about 5 minutes.', timerDuration: 300, timerLabel: 'Sauté' },
      { id: generateId(), order: 3, instruction: 'Add garlic and cook for 30 seconds until fragrant. Stir in crushed tomatoes, tomato paste, salt, and pepper. Simmer for 5 minutes.', timerDuration: 300, timerLabel: 'Sauce' },
      { id: generateId(), order: 4, instruction: 'Spread sauce evenly across the bottom of the skillet.' },
      { id: generateId(), order: 5, instruction: 'Arrange alternating slices of zucchini, eggplant, yellow squash, and tomato in a tight spiral pattern on top of the sauce.' },
      { id: generateId(), order: 6, instruction: 'Drizzle remaining olive oil over the vegetables. Season with salt, pepper, and thyme leaves. Cover tightly with foil.' },
      { id: generateId(), order: 7, instruction: 'Bake covered for 30 minutes until vegetables are tender.', timerDuration: 1800, timerLabel: 'Bake covered' },
      { id: generateId(), order: 8, instruction: 'Remove foil and bake for another 15 minutes until edges are lightly caramelized.', timerDuration: 900, timerLabel: 'Bake uncovered' },
      { id: generateId(), order: 9, instruction: 'Let rest for 5 minutes. Garnish with torn fresh basil and serve warm.' },
    ],
    notes: 'Slice the vegetables as evenly as possible — a mandoline helps. Ratatouille tastes even better the next day as the flavors meld. Serve with crusty bread or over couscous.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  // ── Italian ─────────────────────────────────────────────────────────
  {
    id: generateId(),
    name: 'Pasta Aglio e Olio',
    description: 'The iconic Roman midnight pasta — spaghetti tossed with golden garlic, chili flakes, and fruity olive oil. Five ingredients, ten minutes, pure magic.',
    imageUrl: '/recipe-images/seed/pasta-aglio-e-olio.webp',
    prepTime: 5 * 60,
    cookTime: 12 * 60,
    totalTime: 17 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'italian',
    tags: ['pasta', 'quick', 'weeknight', 'classic'],
    dietaryTags: ['vegan', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Spaghetti', amount: 400, unit: 'g' },
      { id: generateId(), name: 'Extra virgin olive oil', amount: 6, unit: 'tbsp', notes: 'good quality' },
      { id: generateId(), name: 'Garlic', amount: 8, unit: 'clove', notes: 'thinly sliced' },
      { id: generateId(), name: 'Red chili flakes', amount: 1, unit: 'tsp', notes: 'adjust to taste' },
      { id: generateId(), name: 'Fresh parsley', amount: 0.25, unit: 'cup', notes: 'roughly chopped' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'to taste', notes: 'for pasta water' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Bring a large pot of generously salted water to a boil. Cook spaghetti until just shy of al dente — it will finish cooking in the oil.', timerDuration: 420, timerLabel: 'Pasta' },
      { id: generateId(), order: 2, instruction: 'While pasta cooks, heat olive oil in a large skillet over medium-low heat. Add sliced garlic and chili flakes.' },
      { id: generateId(), order: 3, instruction: 'Cook garlic slowly until pale golden and fragrant, about 3 minutes. Stir often — burnt garlic is bitter and ruins the dish.', timerDuration: 180, timerLabel: 'Garlic' },
      { id: generateId(), order: 4, instruction: 'Reserve 1 cup of starchy pasta water before draining the spaghetti.' },
      { id: generateId(), order: 5, instruction: 'Add drained pasta directly to the garlic oil. Toss vigorously over medium heat, adding pasta water a splash at a time.' },
      { id: generateId(), order: 6, instruction: 'Keep tossing for 1-2 minutes until the oil and pasta water emulsify into a silky, clingy sauce that coats each strand.' },
      { id: generateId(), order: 7, instruction: 'Remove from heat, toss in chopped parsley, and serve immediately.' },
    ],
    notes: 'The starchy pasta water is the secret — it emulsifies with the oil to create a real sauce, not just greasy noodles. Never rinse your pasta. Slice the garlic thin and even so it cooks uniformly.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Caprese Salad',
    description: 'Thick slices of ripe tomatoes, fresh mozzarella, and fragrant basil drizzled with the best olive oil you have. The simplest Italian salad — and the hardest to improve.',
    imageUrl: '/recipe-images/seed/caprese-salad.webp',
    prepTime: 10 * 60,
    cookTime: 0,
    totalTime: 10 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'italian',
    tags: ['salad', 'no-cook', 'quick', 'summer'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Fresh mozzarella', amount: 250, unit: 'g', notes: 'ball or burrata, sliced 1cm thick' },
      { id: generateId(), name: 'Ripe tomatoes', amount: 4, unit: 'piece', notes: 'large, sliced 1cm thick' },
      { id: generateId(), name: 'Fresh basil', amount: 1, unit: 'bunch', notes: 'leaves picked' },
      { id: generateId(), name: 'Extra virgin olive oil', amount: 3, unit: 'tbsp', notes: 'best quality' },
      { id: generateId(), name: 'Flaky sea salt', amount: 1, unit: 'pinch' },
      { id: generateId(), name: 'Black pepper', amount: 1, unit: 'pinch', notes: 'freshly cracked' },
      { id: generateId(), name: 'Balsamic glaze', amount: 1, unit: 'tbsp', notes: 'for drizzling', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Take mozzarella out of the fridge 20 minutes before serving — it tastes best at room temperature.' },
      { id: generateId(), order: 2, instruction: 'Slice tomatoes and mozzarella into even 1cm-thick rounds.' },
      { id: generateId(), order: 3, instruction: 'Arrange alternating slices of tomato and mozzarella on a serving plate, overlapping slightly.' },
      { id: generateId(), order: 4, instruction: 'Tuck fresh basil leaves between the slices.' },
      { id: generateId(), order: 5, instruction: 'Drizzle generously with olive oil. Season with flaky salt and cracked pepper.' },
      { id: generateId(), order: 6, instruction: 'Add balsamic glaze in a thin zigzag if using. Serve immediately — don\'t let it sit.' },
    ],
    notes: 'This dish lives and dies by ingredient quality. Use the ripest summer tomatoes you can find, real buffalo mozzarella, and your best olive oil. Never refrigerate a finished Caprese.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  // ── Snacks ──────────────────────────────────────────────────────────
  {
    id: generateId(),
    name: 'Classic Bruschetta',
    description: 'Toasted garlic bread piled high with fresh tomatoes, basil, and olive oil. The ultimate Italian appetizer that takes minutes to make.',
    imageUrl: '/recipe-images/seed/classic-bruschetta.webp',
    prepTime: 10 * 60,
    cookTime: 5 * 60,
    totalTime: 15 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'italian',
    tags: ['snack', 'appetizer', 'quick', 'party'],
    dietaryTags: ['vegan', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Baguette', amount: 1, unit: 'piece', notes: 'sliced into 1.5cm rounds' },
      { id: generateId(), name: 'Roma tomatoes', amount: 4, unit: 'piece', notes: 'diced small, seeds removed' },
      { id: generateId(), name: 'Fresh basil', amount: 0.25, unit: 'cup', notes: 'finely chopped' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: '1 halved for rubbing, 2 minced for topping' },
      { id: generateId(), name: 'Extra virgin olive oil', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Balsamic vinegar', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Black pepper', amount: 1, unit: 'pinch' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Dice tomatoes, removing seeds and excess juice. Combine with minced garlic, basil, 2 tbsp olive oil, balsamic vinegar, salt, and pepper. Set aside.' },
      { id: generateId(), order: 2, instruction: 'Slice baguette into 1.5cm rounds. Brush both sides lightly with remaining olive oil.' },
      { id: generateId(), order: 3, instruction: 'Toast bread under the broiler or in a hot pan for 1-2 minutes per side until golden and crisp.', timerDuration: 120, timerLabel: 'Toast' },
      { id: generateId(), order: 4, instruction: 'While still warm, rub the halved garlic clove across the surface of each toast — the rough texture acts like a grater.' },
      { id: generateId(), order: 5, instruction: 'Spoon tomato mixture generously onto each toast. Serve immediately so the bread stays crisp.' },
    ],
    notes: 'The key is to top the bread right before serving — if it sits, the bread gets soggy. Use the ripest tomatoes you can find. Season the tomato mixture and taste before spooning.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Stuffed Medjool Dates',
    description: 'Sweet Medjool dates stuffed with cream cheese, sprinkled with pistachios and a drizzle of honey. An elegant no-cook snack ready in 5 minutes.',
    imageUrl: '/recipe-images/seed/stuffed-medjool-dates.webp',
    prepTime: 5 * 60,
    cookTime: 0,
    totalTime: 5 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'middle-eastern',
    tags: ['snack', 'no-cook', 'quick', 'sweet'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Medjool dates', amount: 12, unit: 'piece', notes: 'pitted' },
      { id: generateId(), name: 'Cream cheese', amount: 100, unit: 'g', notes: 'softened' },
      { id: generateId(), name: 'Pistachios', amount: 3, unit: 'tbsp', notes: 'roughly chopped' },
      { id: generateId(), name: 'Honey', amount: 1, unit: 'tbsp', notes: 'for drizzling' },
      { id: generateId(), name: 'Flaky sea salt', amount: 1, unit: 'pinch' },
      { id: generateId(), name: 'Cinnamon', amount: 1, unit: 'pinch', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Slice each date lengthwise along one side. Remove the pit if not already pitted. Open the date like a book.' },
      { id: generateId(), order: 2, instruction: 'Fill each date with about 1 teaspoon of softened cream cheese, pressing gently to nestle it in.' },
      { id: generateId(), order: 3, instruction: 'Arrange stuffed dates on a plate. Sprinkle chopped pistachios on top, pressing lightly so they stick.' },
      { id: generateId(), order: 4, instruction: 'Drizzle with honey and finish with a pinch of flaky sea salt and optional cinnamon.' },
      { id: generateId(), order: 5, instruction: 'Serve immediately or chill for 10 minutes for a firmer texture.' },
    ],
    notes: 'You can swap cream cheese for mascarpone, nut butter, or goat cheese. These can be made a few hours ahead — refrigerate uncovered and drizzle honey just before serving.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Garlic Parmesan Pita Chips',
    description: 'Crispy, golden pita triangles tossed with garlic butter and Parmesan. Addictively crunchy and ready in under 15 minutes — perfect for dipping or snacking.',
    imageUrl: '/recipe-images/seed/garlic-parmesan-pita-chips.webp',
    prepTime: 5 * 60,
    cookTime: 8 * 60,
    totalTime: 13 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'mediterranean',
    tags: ['snack', 'appetizer', 'quick', 'party'],
    dietaryTags: ['vegetarian', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Pita bread', amount: 4, unit: 'piece', notes: 'large rounds' },
      { id: generateId(), name: 'Unsalted butter', amount: 3, unit: 'tbsp', notes: 'melted' },
      { id: generateId(), name: 'Olive oil', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Garlic powder', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Parmesan', amount: 3, unit: 'tbsp', notes: 'finely grated' },
      { id: generateId(), name: 'Dried oregano', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Fresh parsley', amount: 1, unit: 'tbsp', notes: 'finely chopped, for garnish', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Preheat oven to 200°C (400°F). Line a baking sheet with parchment paper.' },
      { id: generateId(), order: 2, instruction: 'Cut each pita into 8 triangles. If pitas are thick, separate each triangle into 2 thinner pieces for extra crispiness.' },
      { id: generateId(), order: 3, instruction: 'Mix melted butter, olive oil, garlic powder, oregano, and salt in a bowl.' },
      { id: generateId(), order: 4, instruction: 'Toss pita triangles in the butter mixture until evenly coated. Spread in a single layer on the baking sheet.' },
      { id: generateId(), order: 5, instruction: 'Sprinkle grated Parmesan evenly over the pita chips.' },
      { id: generateId(), order: 6, instruction: 'Bake for 8-10 minutes until golden and crisp. Flip halfway through for even browning.', timerDuration: 480, timerLabel: 'Bake' },
      { id: generateId(), order: 7, instruction: 'Remove from oven and sprinkle with parsley if using. Let cool for 2 minutes — they crisp up further as they cool.' },
    ],
    notes: 'These stay crisp in an airtight container for 2-3 days. Serve with hummus, labneh, or tzatziki. For a spicy kick, add 0.5 tsp chili flakes to the butter mixture.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Cucumber Cream Cheese Bites',
    description: 'Cool cucumber rounds topped with herbed cream cheese and everything seasoning. A refreshing no-cook snack that looks as good as it tastes.',
    imageUrl: '/recipe-images/seed/cucumber-cream-cheese-bites.webp',
    prepTime: 10 * 60,
    cookTime: 0,
    totalTime: 10 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'other',
    tags: ['snack', 'no-cook', 'quick', 'healthy'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'English cucumber', amount: 2, unit: 'piece', notes: 'sliced into 1.5cm rounds' },
      { id: generateId(), name: 'Cream cheese', amount: 120, unit: 'g', notes: 'softened' },
      { id: generateId(), name: 'Fresh dill', amount: 1, unit: 'tbsp', notes: 'finely chopped' },
      { id: generateId(), name: 'Lemon zest', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Garlic powder', amount: 0.25, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'pinch' },
      { id: generateId(), name: 'Everything bagel seasoning', amount: 2, unit: 'tsp', notes: 'for topping' },
      { id: generateId(), name: 'Cherry tomatoes', amount: 8, unit: 'piece', notes: 'halved, for garnish', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Mix softened cream cheese with chopped dill, lemon zest, garlic powder, and salt until smooth.' },
      { id: generateId(), order: 2, instruction: 'Slice cucumbers into 1.5cm-thick rounds. Pat dry with paper towels to prevent the topping from sliding.' },
      { id: generateId(), order: 3, instruction: 'Pipe or spoon about 1 teaspoon of the cream cheese mixture onto each cucumber round.' },
      { id: generateId(), order: 4, instruction: 'Sprinkle everything bagel seasoning on top. Add a halved cherry tomato to each if using.' },
      { id: generateId(), order: 5, instruction: 'Arrange on a plate and serve immediately.' },
    ],
    notes: 'For a make-ahead version, slice cucumbers and prepare the cream cheese mixture separately. Assemble just before serving to keep the cucumbers crisp.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  // ── Trending 2025-2026 ──────────────────────────────────────────────
  {
    id: generateId(),
    name: 'Dubai Chocolate Bar',
    description: 'The viral pistachio chocolate sensation — crunchy kataifi pastry threads and a silky pistachio-tahini filling wrapped in rich dark chocolate. The TikTok obsession, homemade.',
    imageUrl: '/recipe-images/seed/dubai-chocolate-bar.webp',
    prepTime: 30 * 60,
    cookTime: 10 * 60,
    totalTime: 120 * 60, // includes chilling
    servings: 8,
    difficulty: 'medium',
    cuisine: 'middle-eastern',
    tags: ['dessert', 'sweet', 'viral', 'chocolate'],
    dietaryTags: ['vegetarian', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Dark chocolate', amount: 300, unit: 'g', notes: 'roughly chopped, 70% cocoa' },
      { id: generateId(), name: 'Kataifi pastry', amount: 150, unit: 'g', notes: 'thawed if frozen' },
      { id: generateId(), name: 'Unsalted butter', amount: 2, unit: 'tbsp', notes: 'melted', group: 'Kataifi' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tbsp', group: 'Kataifi' },
      { id: generateId(), name: 'Pistachio butter', amount: 150, unit: 'g', notes: 'or blended pistachios', group: 'Filling' },
      { id: generateId(), name: 'Tahini', amount: 3, unit: 'tbsp', group: 'Filling' },
      { id: generateId(), name: 'Honey', amount: 2, unit: 'tbsp', group: 'Filling' },
      { id: generateId(), name: 'Pistachios', amount: 50, unit: 'g', notes: 'roughly chopped, for texture', group: 'Filling' },
      { id: generateId(), name: 'Flaky sea salt', amount: 1, unit: 'pinch' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Preheat oven to 180°C (350°F). Separate kataifi strands and toss with melted butter and sugar. Spread on a baking sheet.' },
      { id: generateId(), order: 2, instruction: 'Bake kataifi for 8-10 minutes, tossing halfway, until golden and crispy. Watch closely — it burns fast.', timerDuration: 480, timerLabel: 'Kataifi' },
      { id: generateId(), order: 3, instruction: 'Let kataifi cool completely. It will crisp up further as it cools.' },
      { id: generateId(), order: 4, instruction: 'Mix pistachio butter, tahini, honey, and chopped pistachios in a bowl until combined.' },
      { id: generateId(), order: 5, instruction: 'Melt two-thirds of the chocolate in a heatproof bowl over simmering water, stirring until smooth. Remove from heat and stir in remaining chocolate until fully melted and glossy.' },
      { id: generateId(), order: 6, instruction: 'Line a rectangular mold or loaf pan with parchment paper. Pour a thin layer of melted chocolate on the bottom and spread evenly. Refrigerate for 5 minutes until just set.' },
      { id: generateId(), order: 7, instruction: 'Spread the pistachio-tahini filling in an even layer. Press a generous layer of crispy kataifi on top.' },
      { id: generateId(), order: 8, instruction: 'Pour remaining melted chocolate over the kataifi layer, covering completely. Tap the mold to release air bubbles.' },
      { id: generateId(), order: 9, instruction: 'Sprinkle with flaky sea salt. Refrigerate for at least 1 hour until fully set.', timerDuration: 3600, timerLabel: 'Chill' },
      { id: generateId(), order: 10, instruction: 'Lift out of the mold using the parchment. Cut into bars with a sharp knife. Let sit at room temperature for 5 minutes before eating for the best texture.' },
    ],
    notes: 'If you can\'t find kataifi pastry, shredded phyllo dough works. For pistachio butter, blend roasted pistachios in a food processor for 5-8 minutes until smooth. The bars keep in the fridge for up to a week.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Viral Carrot Ribbon Salad',
    description: 'The TikTok salad that made the world crave vegetables. Thin carrot ribbons tossed in a garlicky, sesame-chili dressing with crunchy toppings. Dangerously addictive.',
    imageUrl: '/recipe-images/seed/viral-carrot-ribbon-salad.webp',
    prepTime: 15 * 60,
    cookTime: 0,
    totalTime: 15 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'korean',
    tags: ['salad', 'healthy', 'viral', 'no-cook', 'quick'],
    dietaryTags: ['vegan', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Large carrots', amount: 6, unit: 'piece', notes: 'peeled into ribbons with a vegetable peeler' },
      { id: generateId(), name: 'Green onions', amount: 3, unit: 'piece', notes: 'thinly sliced' },
      { id: generateId(), name: 'Sesame seeds', amount: 2, unit: 'tbsp', notes: 'toasted' },
      { id: generateId(), name: 'Soy sauce', amount: 3, unit: 'tbsp', group: 'Dressing' },
      { id: generateId(), name: 'Sesame oil', amount: 2, unit: 'tbsp', group: 'Dressing' },
      { id: generateId(), name: 'Rice vinegar', amount: 2, unit: 'tbsp', group: 'Dressing' },
      { id: generateId(), name: 'Chili crisp oil', amount: 2, unit: 'tbsp', notes: 'adjust to taste', group: 'Dressing' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: 'finely grated', group: 'Dressing' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tsp', group: 'Dressing' },
      { id: generateId(), name: 'Roasted peanuts', amount: 3, unit: 'tbsp', notes: 'roughly chopped', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Peel carrots into long thin ribbons using a vegetable peeler. Rotate the carrot as you peel to get even ribbons from all sides. Discard the thin core.' },
      { id: generateId(), order: 2, instruction: 'Place carrot ribbons in a large bowl of ice water for 5 minutes to crisp up and curl. Drain and pat very dry.', timerDuration: 300, timerLabel: 'Ice bath' },
      { id: generateId(), order: 3, instruction: 'Make the dressing: whisk together soy sauce, sesame oil, rice vinegar, chili crisp, grated garlic, and sugar until combined.' },
      { id: generateId(), order: 4, instruction: 'Pour dressing over the dried carrot ribbons. Toss gently with your hands to coat every ribbon.' },
      { id: generateId(), order: 5, instruction: 'Add sliced green onions and half the sesame seeds. Toss again.' },
      { id: generateId(), order: 6, instruction: 'Transfer to a serving plate. Top with remaining sesame seeds and chopped peanuts if using. Serve immediately.' },
    ],
    notes: 'The ice water bath is the secret — it makes the ribbons curl and gives them a satisfying crunch. Dry the ribbons thoroughly or the dressing gets diluted. This salad is best eaten fresh — it wilts if it sits too long.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Lasagna Soup',
    description: 'All the comfort of lasagna in a cozy one-pot soup — seasoned beef, broken lasagna noodles, rich tomato broth, and melty ricotta dollops. The viral 15-million-view TikTok recipe.',
    imageUrl: '/recipe-images/seed/lasagna-soup.webp',
    prepTime: 10 * 60,
    cookTime: 25 * 60,
    totalTime: 35 * 60,
    servings: 6,
    difficulty: 'easy',
    cuisine: 'italian',
    tags: ['soup', 'comfort food', 'viral', 'weeknight', 'one-pot'],
    dietaryTags: ['halal'],
    ingredients: [
      { id: generateId(), name: 'Ground beef', amount: 500, unit: 'g', notes: 'halal' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'diced' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Crushed tomatoes', amount: 800, unit: 'g', notes: '2 cans' },
      { id: generateId(), name: 'Beef broth', amount: 1, unit: 'l', notes: 'halal certified' },
      { id: generateId(), name: 'Tomato paste', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Dried oregano', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Dried basil', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Red chili flakes', amount: 0.25, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Lasagna noodles', amount: 200, unit: 'g', notes: 'broken into pieces' },
      { id: generateId(), name: 'Ricotta cheese', amount: 200, unit: 'g', group: 'Topping' },
      { id: generateId(), name: 'Mozzarella', amount: 150, unit: 'g', notes: 'shredded', group: 'Topping' },
      { id: generateId(), name: 'Parmesan', amount: 50, unit: 'g', notes: 'grated', group: 'Topping' },
      { id: generateId(), name: 'Fresh basil', amount: 0.25, unit: 'cup', notes: 'torn', group: 'Topping' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Heat a large pot over medium-high heat. Add ground beef and cook, breaking it up with a spoon, until browned and no longer pink, about 6 minutes.', timerDuration: 360, timerLabel: 'Brown beef' },
      { id: generateId(), order: 2, instruction: 'Add diced onion and cook for 3 minutes until softened. Add garlic and cook 30 seconds until fragrant.' },
      { id: generateId(), order: 3, instruction: 'Stir in tomato paste, oregano, dried basil, chili flakes, salt, and pepper. Cook for 1 minute.' },
      { id: generateId(), order: 4, instruction: 'Pour in crushed tomatoes and beef broth. Stir well and bring to a boil.' },
      { id: generateId(), order: 5, instruction: 'Add broken lasagna noodles. Stir to prevent sticking. Reduce heat and simmer until noodles are tender, about 12 minutes.', timerDuration: 720, timerLabel: 'Noodles' },
      { id: generateId(), order: 6, instruction: 'Taste the broth and adjust seasoning. The soup will thicken as it sits — add a splash of broth if needed.' },
      { id: generateId(), order: 7, instruction: 'Ladle soup into bowls. Drop generous spoonfuls of ricotta on top, sprinkle with mozzarella and Parmesan.' },
      { id: generateId(), order: 8, instruction: 'Garnish with torn fresh basil. Serve immediately while the cheese is melty.' },
    ],
    notes: 'Break the lasagna noodles into irregular pieces about 5cm long — don\'t make them too small or they\'ll disintegrate. The ricotta dollops are essential — stir them into the hot soup as you eat for a creamy finish.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Smash Burger Tacos',
    description: 'Crispy-edged beef smashed directly onto a tortilla with melted cheese — the genius fusion of a smash burger and a taco that broke the internet.',
    imageUrl: '/recipe-images/seed/smash-burger-tacos.webp',
    prepTime: 10 * 60,
    cookTime: 10 * 60,
    totalTime: 20 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'mexican',
    tags: ['tacos', 'quick', 'viral', 'weeknight', 'street food'],
    dietaryTags: ['gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Ground beef', amount: 500, unit: 'g', notes: 'halal, 80/20 blend' },
      { id: generateId(), name: 'Small flour or corn tortillas', amount: 8, unit: 'piece' },
      { id: generateId(), name: 'Cheddar cheese', amount: 150, unit: 'g', notes: 'shredded' },
      { id: generateId(), name: 'Onion powder', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Garlic powder', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Vegetable oil', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Shredded lettuce', amount: 1, unit: 'cup', group: 'Toppings' },
      { id: generateId(), name: 'Diced tomato', amount: 1, unit: 'piece', group: 'Toppings' },
      { id: generateId(), name: 'Pickled jalapeños', amount: 3, unit: 'tbsp', notes: 'sliced', optional: true, group: 'Toppings' },
      { id: generateId(), name: 'Sour cream', amount: 4, unit: 'tbsp', group: 'Toppings' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Divide ground beef into 8 equal balls (about 60g each). Season all over with onion powder, garlic powder, salt, and pepper.' },
      { id: generateId(), order: 2, instruction: 'Heat a flat griddle or large cast-iron skillet over high heat until smoking hot. Add a thin layer of oil.' },
      { id: generateId(), order: 3, instruction: 'Place a beef ball on the griddle. Immediately press a tortilla on top and smash down hard with a sturdy spatula or press until very thin.' },
      { id: generateId(), order: 4, instruction: 'Cook for 2 minutes until the beef is deeply browned and crispy on the bottom. The beef fuses to the tortilla.', timerDuration: 120, timerLabel: 'Smash' },
      { id: generateId(), order: 5, instruction: 'Add a pinch of shredded cheese on the meat. Flip the whole taco over so the tortilla is now on the bottom.' },
      { id: generateId(), order: 6, instruction: 'Cook for 1 more minute until the tortilla is toasted and cheese is melted.', timerDuration: 60, timerLabel: 'Flip side' },
      { id: generateId(), order: 7, instruction: 'Transfer to a plate and fold in half. Repeat with remaining beef balls and tortillas.' },
      { id: generateId(), order: 8, instruction: 'Top with shredded lettuce, diced tomato, jalapeños, and a drizzle of sour cream. Serve immediately.' },
    ],
    notes: 'The key is a screaming-hot griddle and smashing the meat as thin as possible — that\'s what creates the crispy, lacy edges. Work in batches of 2-3 at a time. Don\'t overcrowd or the meat steams instead of crisping.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Japanese Soufflé Pancakes',
    description: 'Impossibly fluffy, jiggly Japanese-style pancakes that bounce when you touch them. Made with a cloud-like meringue folded into pancake batter — the most satisfying breakfast on the internet.',
    imageUrl: '/recipe-images/seed/japanese-souffle-pancakes.webp',
    prepTime: 15 * 60,
    cookTime: 20 * 60,
    totalTime: 35 * 60,
    servings: 2,
    difficulty: 'medium',
    cuisine: 'japanese',
    tags: ['breakfast', 'sweet', 'viral', 'technique'],
    dietaryTags: ['vegetarian', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Egg yolks', amount: 2, unit: 'piece', notes: 'room temperature' },
      { id: generateId(), name: 'Egg whites', amount: 3, unit: 'piece', notes: 'room temperature, must be grease-free' },
      { id: generateId(), name: 'All-purpose flour', amount: 30, unit: 'g' },
      { id: generateId(), name: 'Milk', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Vanilla extract', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Sugar', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Lemon juice', amount: 0.5, unit: 'tsp', notes: 'or cream of tartar, for meringue' },
      { id: generateId(), name: 'Unsalted butter', amount: 1, unit: 'tsp', notes: 'for the pan' },
      { id: generateId(), name: 'Maple syrup', amount: 3, unit: 'tbsp', notes: 'for serving', group: 'Topping' },
      { id: generateId(), name: 'Powdered sugar', amount: 1, unit: 'tbsp', notes: 'for dusting', group: 'Topping' },
      { id: generateId(), name: 'Fresh berries', amount: 0.5, unit: 'cup', optional: true, group: 'Topping' },
      { id: generateId(), name: 'Whipped cream', amount: 4, unit: 'tbsp', optional: true, group: 'Topping' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'In a large bowl, whisk egg yolks with milk and vanilla until smooth. Sift in flour and whisk until no lumps remain.' },
      { id: generateId(), order: 2, instruction: 'In a separate clean, dry bowl, beat egg whites with lemon juice using an electric mixer on medium speed until foamy. Gradually add sugar.' },
      { id: generateId(), order: 3, instruction: 'Continue beating on high until stiff, glossy peaks form — the meringue should hold its shape when you lift the beaters.' },
      { id: generateId(), order: 4, instruction: 'Fold one-third of the meringue into the yolk batter vigorously to lighten it. Then gently fold in the remaining meringue in two additions — use a cut-and-fold motion to keep the air.' },
      { id: generateId(), order: 5, instruction: 'Heat a non-stick pan over the lowest heat setting. Brush with a very thin layer of butter. Place ring molds if you have them.' },
      { id: generateId(), order: 6, instruction: 'Spoon batter into tall mounds (about 3cm high). Add 1 tbsp of water to the pan and cover with a lid immediately.', notes: 'The steam helps cook the pancakes through without burning the bottom.' },
      { id: generateId(), order: 7, instruction: 'Cook covered on the lowest heat for 7-8 minutes until the bottoms are golden and the surface is starting to set.', timerDuration: 480, timerLabel: 'Side 1' },
      { id: generateId(), order: 8, instruction: 'Carefully flip each pancake. Add another tbsp of water, cover, and cook for 5-6 more minutes.', timerDuration: 360, timerLabel: 'Side 2' },
      { id: generateId(), order: 9, instruction: 'Slide onto plates immediately — they deflate as they cool. Dust with powdered sugar, top with berries and whipped cream if using, and drizzle with maple syrup.' },
    ],
    notes: 'The three secrets: 1) Lowest heat possible — these cook with steam, not direct heat. 2) Stiff meringue — underwhipped whites = flat pancakes. 3) Gentle folding — overmixing deflates the air. Eat immediately, they deflate in minutes!',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Chili Crisp Fried Eggs',
    description: 'Crispy-edged fried eggs drenched in sizzling chili crisp oil on thick toast. The 5-minute breakfast that took over TikTok — spicy, crunchy, and utterly satisfying.',
    imageUrl: '/recipe-images/seed/chili-crisp-fried-eggs.webp',
    prepTime: 2 * 60,
    cookTime: 3 * 60,
    totalTime: 5 * 60,
    servings: 1,
    difficulty: 'easy',
    cuisine: 'chinese',
    tags: ['breakfast', 'quick', 'viral', 'spicy', 'snack'],
    dietaryTags: ['dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Eggs', amount: 2, unit: 'piece' },
      { id: generateId(), name: 'Chili crisp oil', amount: 2, unit: 'tbsp', notes: 'Lao Gan Ma or similar' },
      { id: generateId(), name: 'Sourdough bread', amount: 2, unit: 'slice', notes: 'thick-cut, toasted' },
      { id: generateId(), name: 'Soy sauce', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Sesame seeds', amount: 0.5, unit: 'tsp', optional: true },
      { id: generateId(), name: 'Green onion', amount: 1, unit: 'piece', notes: 'sliced', optional: true },
      { id: generateId(), name: 'Avocado', amount: 0.5, unit: 'piece', notes: 'sliced', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Toast your bread until golden and crisp. Set aside.' },
      { id: generateId(), order: 2, instruction: 'Heat chili crisp oil in a small non-stick pan over medium-high heat until the oil is shimmering and fragrant.' },
      { id: generateId(), order: 3, instruction: 'Crack eggs directly into the hot chili oil. The edges will bubble and crisp immediately.' },
      { id: generateId(), order: 4, instruction: 'Fry for 2-3 minutes until whites are set and edges are golden and lacy, but yolks are still runny.', timerDuration: 150, timerLabel: 'Fry eggs' },
      { id: generateId(), order: 5, instruction: 'Drizzle soy sauce over the eggs in the pan. Slide eggs onto the toast, spooning all the chili oil from the pan over the top.' },
      { id: generateId(), order: 6, instruction: 'Top with avocado slices, sesame seeds, and green onion if using. Eat immediately.' },
    ],
    notes: 'The magic is frying the eggs IN the chili crisp — the crunchy bits get embedded in the egg whites. Keep the yolks runny so they break and mix with the spicy oil when you eat.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'One-Pot French Onion Pasta',
    description: 'The beloved flavors of French onion soup turned into a creamy, cheesy one-pot pasta. Caramelized onions, rich broth, and melted Gruyère clinging to every noodle.',
    imageUrl: '/recipe-images/seed/one-pot-french-onion-pasta.webp',
    prepTime: 10 * 60,
    cookTime: 40 * 60,
    totalTime: 50 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'french',
    tags: ['pasta', 'comfort food', 'viral', 'one-pot', 'dinner'],
    dietaryTags: ['halal'],
    ingredients: [
      { id: generateId(), name: 'Yellow onions', amount: 4, unit: 'piece', notes: 'large, thinly sliced' },
      { id: generateId(), name: 'Unsalted butter', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Olive oil', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Fresh thyme', amount: 1, unit: 'tbsp', notes: 'leaves only' },
      { id: generateId(), name: 'Beef broth', amount: 750, unit: 'ml', notes: 'halal certified' },
      { id: generateId(), name: 'Heavy cream', amount: 120, unit: 'ml' },
      { id: generateId(), name: 'Short pasta', amount: 350, unit: 'g', notes: 'penne, rigatoni, or cavatappi' },
      { id: generateId(), name: 'Gruyère cheese', amount: 150, unit: 'g', notes: 'grated' },
      { id: generateId(), name: 'Parmesan', amount: 50, unit: 'g', notes: 'grated' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Fresh parsley', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Melt butter with olive oil in a large heavy pot over medium heat. Add all sliced onions and salt. Stir to coat.' },
      { id: generateId(), order: 2, instruction: 'Cook onions for 25-30 minutes, stirring every 5 minutes, until deeply golden and caramelized. Lower heat if they brown too quickly.', timerDuration: 1500, timerLabel: 'Caramelize' },
      { id: generateId(), order: 3, instruction: 'Add garlic and thyme. Cook for 1 minute until fragrant.' },
      { id: generateId(), order: 4, instruction: 'Pour in beef broth and cream. Bring to a boil, scraping up any browned bits from the bottom.' },
      { id: generateId(), order: 5, instruction: 'Add pasta and stir well. Reduce heat, cover, and simmer until pasta is al dente and has absorbed most of the liquid, about 12-14 minutes. Stir halfway through.', timerDuration: 780, timerLabel: 'Pasta' },
      { id: generateId(), order: 6, instruction: 'Remove from heat. Stir in two-thirds of the Gruyère and all the Parmesan until melted and creamy. Season with pepper.' },
      { id: generateId(), order: 7, instruction: 'Top with remaining Gruyère. Cover for 2 minutes to let it melt, or broil for 2 minutes for a golden crust.', timerDuration: 120, timerLabel: 'Melt cheese' },
      { id: generateId(), order: 8, instruction: 'Garnish with fresh parsley if using. Serve hot from the pot.' },
    ],
    notes: 'Don\'t skip the caramelization — it\'s the soul of the dish. If the pasta absorbs too much liquid, splash in more broth. Leftovers thicken overnight; reheat with a little extra broth.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Custard Toast',
    description: 'A thick slice of bread with a creamy egg custard center baked until the edges are crispy and the middle is silky smooth. Korean street food magic that went viral on TikTok.',
    imageUrl: '/recipe-images/seed/custard-toast.webp',
    prepTime: 5 * 60,
    cookTime: 12 * 60,
    totalTime: 17 * 60,
    servings: 2,
    difficulty: 'easy',
    cuisine: 'korean',
    tags: ['breakfast', 'sweet', 'viral', 'quick', 'snack'],
    dietaryTags: ['vegetarian', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Thick-cut bread', amount: 2, unit: 'slice', notes: 'brioche or shokupan, at least 3cm thick' },
      { id: generateId(), name: 'Egg', amount: 1, unit: 'piece' },
      { id: generateId(), name: 'Greek yogurt', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tbsp' },
      { id: generateId(), name: 'Vanilla extract', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Unsalted butter', amount: 1, unit: 'tbsp', notes: 'softened' },
      { id: generateId(), name: 'Powdered sugar', amount: 1, unit: 'tsp', notes: 'for dusting' },
      { id: generateId(), name: 'Honey', amount: 1, unit: 'tbsp', notes: 'for drizzling', optional: true },
      { id: generateId(), name: 'Fresh berries', amount: 0.25, unit: 'cup', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Preheat oven to 200°C (400°F). Line a small baking sheet with parchment paper.' },
      { id: generateId(), order: 2, instruction: 'Using the back of a spoon, press a deep well into each bread slice, leaving a 1.5cm border. Don\'t press all the way through.' },
      { id: generateId(), order: 3, instruction: 'Spread softened butter on the raised edges and inside the well of each slice.' },
      { id: generateId(), order: 4, instruction: 'Whisk together egg, Greek yogurt, sugar, and vanilla until smooth and creamy.' },
      { id: generateId(), order: 5, instruction: 'Pour the custard mixture evenly into the bread wells. It should sit inside the depression, not overflow.' },
      { id: generateId(), order: 6, instruction: 'Bake for 10-12 minutes until the bread edges are deeply golden and the custard is just set with a slight jiggle in the center.', timerDuration: 660, timerLabel: 'Bake' },
      { id: generateId(), order: 7, instruction: 'Dust with powdered sugar, drizzle with honey if using, and add fresh berries. Eat warm.' },
    ],
    notes: 'The thicker the bread, the better the result — thin bread won\'t hold the custard. Japanese milk bread (shokupan) or brioche works best. Don\'t overbake — the custard should have a slight wobble when done.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Greek Sheet Pan Chicken',
    description: 'Juicy lemon-herb chicken thighs roasted on a bed of colorful Mediterranean vegetables — all on one pan. The weeknight dinner that was the most-read recipe of 2025.',
    imageUrl: '/recipe-images/seed/greek-sheet-pan-chicken.webp',
    prepTime: 15 * 60,
    cookTime: 35 * 60,
    totalTime: 50 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'greek',
    tags: ['dinner', 'healthy', 'viral', 'weeknight', 'one-pan'],
    dietaryTags: ['gluten-free', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chicken thighs', amount: 8, unit: 'piece', notes: 'bone-in, skin-on, halal' },
      { id: generateId(), name: 'Red bell pepper', amount: 2, unit: 'piece', notes: 'cut into chunks' },
      { id: generateId(), name: 'Zucchini', amount: 2, unit: 'piece', notes: 'sliced into half-moons' },
      { id: generateId(), name: 'Red onion', amount: 1, unit: 'piece', notes: 'cut into wedges' },
      { id: generateId(), name: 'Cherry tomatoes', amount: 250, unit: 'g', notes: 'whole' },
      { id: generateId(), name: 'Kalamata olives', amount: 80, unit: 'g', notes: 'pitted', optional: true },
      { id: generateId(), name: 'Olive oil', amount: 4, unit: 'tbsp', group: 'Marinade' },
      { id: generateId(), name: 'Lemon juice', amount: 3, unit: 'tbsp', group: 'Marinade' },
      { id: generateId(), name: 'Lemon zest', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced', group: 'Marinade' },
      { id: generateId(), name: 'Dried oregano', amount: 2, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Paprika', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Salt', amount: 1.5, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Black pepper', amount: 0.5, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Feta cheese', amount: 100, unit: 'g', notes: 'crumbled, for serving', optional: true },
      { id: generateId(), name: 'Fresh parsley', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Preheat oven to 220°C (425°F). Line a large sheet pan with parchment paper.' },
      { id: generateId(), order: 2, instruction: 'Whisk together olive oil, lemon juice, lemon zest, garlic, oregano, paprika, salt, and pepper in a large bowl.' },
      { id: generateId(), order: 3, instruction: 'Add chicken thighs to the bowl and toss to coat thoroughly. Set aside.' },
      { id: generateId(), order: 4, instruction: 'Spread bell peppers, zucchini, red onion, tomatoes, and olives on the sheet pan. Drizzle with a bit of the remaining marinade and toss.' },
      { id: generateId(), order: 5, instruction: 'Nestle chicken thighs skin-side up among the vegetables. Pour any remaining marinade over everything.' },
      { id: generateId(), order: 6, instruction: 'Roast for 35-40 minutes until chicken skin is golden and crispy, and juices run clear when pierced. Vegetables should be tender and slightly charred.', timerDuration: 2100, timerLabel: 'Roast' },
      { id: generateId(), order: 7, instruction: 'Remove from oven. Scatter crumbled feta and chopped parsley over everything.' },
      { id: generateId(), order: 8, instruction: 'Squeeze a little fresh lemon over the top. Serve straight from the pan with crusty bread or rice.' },
    ],
    notes: 'Pat the chicken skin dry before marinating for the crispiest skin. Don\'t crowd the pan — use two pans if needed. The roasted tomatoes burst into a natural sauce. Leftovers are incredible in a pita wrap.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Crispy Onion Cheese Chips',
    description: 'Two ingredients. Ten minutes. Impossibly crispy, cheesy onion chips baked or air-fried to golden perfection. The viral snack that Google couldn\'t stop searching for in 2025.',
    imageUrl: '/recipe-images/seed/crispy-onion-cheese-chips.webp',
    prepTime: 5 * 60,
    cookTime: 8 * 60,
    totalTime: 13 * 60,
    servings: 2,
    difficulty: 'easy',
    cuisine: 'american',
    tags: ['snack', 'quick', 'viral', 'low-carb'],
    dietaryTags: ['vegetarian', 'gluten-free', 'low-carb', 'keto', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Yellow onion', amount: 1, unit: 'piece', notes: 'large, sliced into thin rings' },
      { id: generateId(), name: 'Shredded cheese', amount: 150, unit: 'g', notes: 'Parmesan, cheddar, or a mix' },
      { id: generateId(), name: 'Smoked paprika', amount: 0.25, unit: 'tsp', optional: true },
      { id: generateId(), name: 'Garlic powder', amount: 0.25, unit: 'tsp', optional: true },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Preheat oven to 200°C (400°F) or air fryer to 190°C (375°F). Line a baking sheet with parchment paper.' },
      { id: generateId(), order: 2, instruction: 'Separate onion rings. Place small piles of shredded cheese on the parchment, spaced apart. Press 1-2 onion rings into each cheese pile.' },
      { id: generateId(), order: 3, instruction: 'Sprinkle with smoked paprika and garlic powder if using.' },
      { id: generateId(), order: 4, instruction: 'Bake for 8-10 minutes until cheese is bubbly and deeply golden. For air fryer, cook 5-6 minutes.', timerDuration: 480, timerLabel: 'Bake' },
      { id: generateId(), order: 5, instruction: 'Let cool on the pan for 3 minutes — they crisp up dramatically as they cool. Do not touch them while soft or they\'ll fall apart.' },
      { id: generateId(), order: 6, instruction: 'Peel off the parchment carefully. Serve at room temperature for maximum crunch.' },
    ],
    notes: 'Use pre-shredded cheese for the crispiest results — fresh-grated melts too smoothly. Parmesan gives the most crunch, cheddar gives more flavor. These don\'t keep well — eat them fresh.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  // ── Indian ──────────────────────────────────────────────────────────
  {
    id: generateId(),
    name: 'Dal Tadka',
    description: 'Creamy yellow lentils finished with a sizzling tadka of ghee, cumin, garlic, and dried chilies. The everyday Indian comfort food that tastes like a warm hug.',
    imageUrl: '/recipe-images/seed/dal-tadka.webp',
    prepTime: 10 * 60,
    cookTime: 30 * 60,
    totalTime: 40 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'indian',
    tags: ['curry', 'comfort food', 'healthy', 'weeknight'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Yellow lentils (toor dal)', amount: 1, unit: 'cup', notes: 'rinsed until water runs clear' },
      { id: generateId(), name: 'Water', amount: 3, unit: 'cup' },
      { id: generateId(), name: 'Turmeric', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Tomato', amount: 1, unit: 'piece', notes: 'finely chopped' },
      { id: generateId(), name: 'Green chili', amount: 1, unit: 'piece', notes: 'slit lengthwise' },
      { id: generateId(), name: 'Fresh cilantro', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish' },
      { id: generateId(), name: 'Ghee', amount: 2, unit: 'tbsp', group: 'Tadka' },
      { id: generateId(), name: 'Cumin seeds', amount: 1, unit: 'tsp', group: 'Tadka' },
      { id: generateId(), name: 'Mustard seeds', amount: 0.5, unit: 'tsp', group: 'Tadka' },
      { id: generateId(), name: 'Dried red chilies', amount: 2, unit: 'piece', notes: 'whole', group: 'Tadka' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'thinly sliced', group: 'Tadka' },
      { id: generateId(), name: 'Asafoetida (hing)', amount: 1, unit: 'pinch', group: 'Tadka' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Rinse lentils in cold water 3-4 times until the water runs mostly clear. Drain.' },
      { id: generateId(), order: 2, instruction: 'Add lentils, 3 cups water, turmeric, and salt to a pot. Bring to a boil, then reduce heat to low. Skim any foam that rises.' },
      { id: generateId(), order: 3, instruction: 'Simmer partially covered for 20-25 minutes until lentils are completely soft and falling apart. Stir occasionally.', timerDuration: 1200, timerLabel: 'Lentils' },
      { id: generateId(), order: 4, instruction: 'Add chopped tomato and green chili. Simmer for 5 more minutes. Mash lentils with the back of a spoon for a creamy consistency.', timerDuration: 300, timerLabel: 'Tomato' },
      { id: generateId(), order: 5, instruction: 'Make the tadka: heat ghee in a small pan over medium-high heat until very hot.' },
      { id: generateId(), order: 6, instruction: 'Add cumin seeds and mustard seeds — they should splutter immediately. Add dried red chilies and sliced garlic.' },
      { id: generateId(), order: 7, instruction: 'Fry for 30-45 seconds until garlic is golden and fragrant. Add asafoetida, stir once.' },
      { id: generateId(), order: 8, instruction: 'Pour the sizzling tadka directly over the dal — it should crackle and steam. Stir gently to combine.' },
      { id: generateId(), order: 9, instruction: 'Garnish with fresh cilantro. Serve hot with steamed rice or warm naan.' },
    ],
    notes: 'The tadka (tempering) is where all the magic happens — the ghee must be very hot so the spices sizzle on contact. Don\'t skip the asafoetida; even a tiny pinch adds incredible depth. Leftovers thicken overnight; thin with water when reheating.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Palak Paneer',
    description: 'Cubes of golden pan-fried paneer in a vibrant, silky spinach sauce spiced with garam masala, garlic, and a touch of cream. A North Indian vegetarian classic.',
    imageUrl: '/recipe-images/seed/palak-paneer.webp',
    prepTime: 15 * 60,
    cookTime: 25 * 60,
    totalTime: 40 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'indian',
    tags: ['curry', 'vegetarian', 'classic', 'dinner'],
    dietaryTags: ['vegetarian', 'gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Paneer', amount: 250, unit: 'g', notes: 'cut into 2cm cubes' },
      { id: generateId(), name: 'Fresh spinach', amount: 400, unit: 'g', notes: 'washed, stems removed' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'finely chopped' },
      { id: generateId(), name: 'Tomato', amount: 1, unit: 'piece', notes: 'chopped' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'tbsp', notes: 'freshly grated' },
      { id: generateId(), name: 'Green chili', amount: 2, unit: 'piece', notes: 'chopped' },
      { id: generateId(), name: 'Cumin seeds', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Garam masala', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Turmeric', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Coriander powder', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Heavy cream', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Ghee', amount: 2, unit: 'tbsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'to taste' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Blanch spinach in boiling water for 2 minutes. Immediately transfer to an ice bath to lock in the bright green color.', timerDuration: 120, timerLabel: 'Blanch' },
      { id: generateId(), order: 2, instruction: 'Drain spinach and blend into a smooth puree with the green chilies. Set aside.' },
      { id: generateId(), order: 3, instruction: 'Heat 1 tbsp ghee in a pan over medium-high heat. Fry paneer cubes until golden on all sides, about 3 minutes. Remove and set on paper towels.', timerDuration: 180, timerLabel: 'Fry paneer' },
      { id: generateId(), order: 4, instruction: 'In the same pan, add remaining ghee. Add cumin seeds and let them splutter.' },
      { id: generateId(), order: 5, instruction: 'Add chopped onion and sauté until golden, about 4 minutes. Add garlic and ginger, cook for 1 minute until fragrant.' },
      { id: generateId(), order: 6, instruction: 'Add tomato, turmeric, and coriander powder. Cook until tomato breaks down and oil separates, about 3 minutes.' },
      { id: generateId(), order: 7, instruction: 'Pour in the spinach puree. Stir well and simmer for 5 minutes, adding a splash of water if too thick.', timerDuration: 300, timerLabel: 'Simmer' },
      { id: generateId(), order: 8, instruction: 'Add garam masala and salt. Gently fold in the fried paneer cubes and cream. Simmer for 2 more minutes so paneer absorbs the flavors.' },
      { id: generateId(), order: 9, instruction: 'Serve hot with naan, roti, or steamed basmati rice.' },
    ],
    notes: 'The ice bath after blanching is critical — it keeps the spinach vibrant green instead of turning army green. Don\'t overcook the paneer or it becomes rubbery. For a vegan version, use firm tofu and coconut cream.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Chole (Chickpea Curry)',
    description: 'Hearty chickpeas simmered in a spiced onion-tomato gravy darkened with tea and amchur. The beloved Punjabi street food that\'s hearty enough to be a full meal.',
    imageUrl: '/recipe-images/seed/chole-chickpea-curry.webp',
    prepTime: 15 * 60,
    cookTime: 30 * 60,
    totalTime: 45 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'indian',
    tags: ['curry', 'comfort food', 'healthy', 'weeknight'],
    dietaryTags: ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chickpeas', amount: 400, unit: 'g', notes: 'canned, drained and rinsed (or 1 cup dried, soaked overnight)' },
      { id: generateId(), name: 'Onion', amount: 2, unit: 'piece', notes: 'finely chopped' },
      { id: generateId(), name: 'Tomatoes', amount: 3, unit: 'piece', notes: 'pureed' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'tbsp', notes: 'freshly grated' },
      { id: generateId(), name: 'Green chili', amount: 2, unit: 'piece', notes: 'slit' },
      { id: generateId(), name: 'Vegetable oil', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Cumin seeds', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Coriander powder', amount: 2, unit: 'tsp' },
      { id: generateId(), name: 'Cumin powder', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Turmeric', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Red chili powder', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Garam masala', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Amchur (dried mango powder)', amount: 1, unit: 'tsp', notes: 'or 1 tbsp lemon juice' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'to taste' },
      { id: generateId(), name: 'Fresh cilantro', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish' },
      { id: generateId(), name: 'Red onion', amount: 0.5, unit: 'piece', notes: 'sliced into rings, for garnish' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Heat oil in a heavy pot over medium heat. Add cumin seeds and let them splutter for 10 seconds.' },
      { id: generateId(), order: 2, instruction: 'Add chopped onions and cook until deeply golden brown, about 8 minutes. Don\'t rush this — the dark onions give chole its signature color.', timerDuration: 480, timerLabel: 'Onions' },
      { id: generateId(), order: 3, instruction: 'Add garlic, ginger, and green chilies. Cook for 1 minute until fragrant.' },
      { id: generateId(), order: 4, instruction: 'Add coriander, cumin, turmeric, and chili powder. Stir for 30 seconds to bloom the spices — add a splash of water if they stick.' },
      { id: generateId(), order: 5, instruction: 'Pour in pureed tomatoes. Cook until oil separates from the masala and it darkens, about 6 minutes.', timerDuration: 360, timerLabel: 'Masala' },
      { id: generateId(), order: 6, instruction: 'Add drained chickpeas and 1 cup water. Stir well to coat the chickpeas in the masala.' },
      { id: generateId(), order: 7, instruction: 'Bring to a boil, then reduce heat and simmer for 15 minutes until the gravy thickens and chickpeas absorb the flavors. Crush a few chickpeas against the pot to thicken further.', timerDuration: 900, timerLabel: 'Simmer' },
      { id: generateId(), order: 8, instruction: 'Stir in garam masala and amchur. Taste and adjust salt and heat.' },
      { id: generateId(), order: 9, instruction: 'Garnish with fresh cilantro and sliced red onion. Serve hot with bhature, puri, or steamed rice.' },
    ],
    notes: 'The amchur (dried mango powder) adds a tangy depth that makes chole distinctive — lemon juice works as a substitute but the flavor is different. For a darker, richer chole, add a steeped black tea bag to the simmering chickpeas.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Chicken Tikka Masala',
    description: 'Charred, smoky chicken tikka pieces swimming in a rich, creamy tomato-spice sauce. The world\'s most beloved Indian curry — and for very good reason.',
    imageUrl: '/recipe-images/seed/chicken-tikka-masala.webp',
    prepTime: 20 * 60,
    cookTime: 25 * 60,
    totalTime: 45 * 60,
    servings: 4,
    difficulty: 'medium',
    cuisine: 'indian',
    tags: ['curry', 'classic', 'dinner', 'comfort food'],
    dietaryTags: ['gluten-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Chicken thighs', amount: 600, unit: 'g', notes: 'boneless, cut into 3cm pieces', group: 'Marinade' },
      { id: generateId(), name: 'Yogurt', amount: 120, unit: 'g', notes: 'full-fat', group: 'Marinade' },
      { id: generateId(), name: 'Lemon juice', amount: 1, unit: 'tbsp', group: 'Marinade' },
      { id: generateId(), name: 'Garam masala', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Cumin powder', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Turmeric', amount: 0.5, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Red chili powder', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'tsp', group: 'Marinade' },
      { id: generateId(), name: 'Ghee', amount: 2, unit: 'tbsp', group: 'Sauce' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'finely diced', group: 'Sauce' },
      { id: generateId(), name: 'Garlic', amount: 4, unit: 'clove', notes: 'minced', group: 'Sauce' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'tbsp', notes: 'freshly grated', group: 'Sauce' },
      { id: generateId(), name: 'Tomato puree', amount: 400, unit: 'g', notes: 'canned crushed tomatoes', group: 'Sauce' },
      { id: generateId(), name: 'Heavy cream', amount: 120, unit: 'ml', group: 'Sauce' },
      { id: generateId(), name: 'Garam masala', amount: 1.5, unit: 'tsp', group: 'Sauce' },
      { id: generateId(), name: 'Paprika', amount: 1, unit: 'tsp', group: 'Sauce' },
      { id: generateId(), name: 'Sugar', amount: 1, unit: 'tsp', group: 'Sauce' },
      { id: generateId(), name: 'Fresh cilantro', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Mix all marinade ingredients together. Add chicken pieces and coat thoroughly. Marinate for at least 15 minutes (or up to overnight in the fridge).' },
      { id: generateId(), order: 2, instruction: 'Preheat broiler to high. Thread chicken onto skewers or spread on a lined baking sheet.' },
      { id: generateId(), order: 3, instruction: 'Broil chicken for 12-15 minutes, turning once, until charred on the edges and cooked through. Set aside.', timerDuration: 780, timerLabel: 'Broil chicken' },
      { id: generateId(), order: 4, instruction: 'Make the sauce: heat ghee in a large pan over medium heat. Sauté onion until soft and golden, about 5 minutes.' },
      { id: generateId(), order: 5, instruction: 'Add garlic and ginger. Cook for 1 minute until fragrant.' },
      { id: generateId(), order: 6, instruction: 'Add tomato puree, garam masala, paprika, and sugar. Simmer for 10 minutes until sauce darkens and oil starts to separate.', timerDuration: 600, timerLabel: 'Sauce' },
      { id: generateId(), order: 7, instruction: 'Stir in cream and bring to a gentle simmer. Add the broiled chicken pieces with any juices.' },
      { id: generateId(), order: 8, instruction: 'Simmer together for 5 minutes so the chicken absorbs the sauce. Taste and adjust salt.', timerDuration: 300, timerLabel: 'Simmer' },
      { id: generateId(), order: 9, instruction: 'Garnish with fresh cilantro. Serve with naan or basmati rice.' },
    ],
    notes: 'Broiling (or grilling) the chicken gives it the charred smokiness that sets tikka masala apart from regular curry. For extra smoke flavor, hold a piece of lit charcoal in a small bowl nestled in the sauce, drizzle with ghee, and cover for 2 minutes (the dhungar method).',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
  {
    id: generateId(),
    name: 'Aloo Gobi',
    description: 'Golden potatoes and cauliflower florets dry-roasted with turmeric, cumin, and a hint of heat. A simple, satisfying everyday Indian side that punches above its weight.',
    imageUrl: '/recipe-images/seed/aloo-gobi.webp',
    prepTime: 10 * 60,
    cookTime: 25 * 60,
    totalTime: 35 * 60,
    servings: 4,
    difficulty: 'easy',
    cuisine: 'indian',
    tags: ['vegetable', 'healthy', 'weeknight', 'classic'],
    dietaryTags: ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'halal'],
    ingredients: [
      { id: generateId(), name: 'Potatoes', amount: 2, unit: 'piece', notes: 'medium, peeled and cut into 2cm cubes' },
      { id: generateId(), name: 'Cauliflower', amount: 1, unit: 'piece', notes: 'medium head, cut into small florets' },
      { id: generateId(), name: 'Onion', amount: 1, unit: 'piece', notes: 'thinly sliced' },
      { id: generateId(), name: 'Tomato', amount: 1, unit: 'piece', notes: 'chopped' },
      { id: generateId(), name: 'Garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { id: generateId(), name: 'Ginger', amount: 1, unit: 'tsp', notes: 'freshly grated' },
      { id: generateId(), name: 'Green chili', amount: 1, unit: 'piece', notes: 'slit' },
      { id: generateId(), name: 'Vegetable oil', amount: 3, unit: 'tbsp' },
      { id: generateId(), name: 'Cumin seeds', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Turmeric', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Coriander powder', amount: 1, unit: 'tsp' },
      { id: generateId(), name: 'Red chili powder', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Garam masala', amount: 0.5, unit: 'tsp' },
      { id: generateId(), name: 'Salt', amount: 1, unit: 'to taste' },
      { id: generateId(), name: 'Fresh cilantro', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish' },
      { id: generateId(), name: 'Lemon juice', amount: 1, unit: 'tbsp' },
    ],
    steps: [
      { id: generateId(), order: 1, instruction: 'Heat oil in a large pan or kadhai over medium heat. Add cumin seeds and let them splutter for 10 seconds.' },
      { id: generateId(), order: 2, instruction: 'Add sliced onion and cook until light golden, about 4 minutes.' },
      { id: generateId(), order: 3, instruction: 'Add garlic, ginger, and green chili. Stir for 30 seconds until fragrant.' },
      { id: generateId(), order: 4, instruction: 'Add turmeric, coriander, and chili powder. Stir for 15 seconds, then add chopped tomato. Cook until tomato softens, about 2 minutes.' },
      { id: generateId(), order: 5, instruction: 'Add potato cubes. Toss to coat in the spice mixture. Cover and cook for 8 minutes, stirring occasionally, until potatoes are half-tender.', timerDuration: 480, timerLabel: 'Potatoes' },
      { id: generateId(), order: 6, instruction: 'Add cauliflower florets and salt. Toss gently. Cover and cook for 10-12 minutes until both vegetables are tender but not mushy. Stir every 3 minutes.', timerDuration: 660, timerLabel: 'Cauliflower' },
      { id: generateId(), order: 7, instruction: 'Remove lid for the last 3 minutes to let any moisture evaporate and edges get slightly crispy.' },
      { id: generateId(), order: 8, instruction: 'Sprinkle garam masala and lemon juice. Toss gently. Garnish with cilantro and serve hot with roti or as a side dish.' },
    ],
    notes: 'This is a dry curry — there should be no gravy, just spice-coated vegetables. Keep the heat at medium and resist adding water; the covered pan steams the vegetables perfectly. The lemon juice at the end brightens all the flavors.',
    source: 'By RecipeSense',
    createdBy: 'recipesense',
    isPublic: true,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    cookCount: 0,
    schemaVersion: SCHEMA_VERSION,
  },
];

export function getSeedRecipes(): Recipe[] {
  // Return copies with fresh IDs
  return SEED_RECIPES.map(recipe => ({
    ...recipe,
    id: generateId(),
    ingredients: recipe.ingredients.map(ing => ({ ...ing, id: generateId() })),
    steps: recipe.steps.map(step => ({ ...step, id: generateId() })),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }));
}
